// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the board
//  ---------------------------------------------------------------------------
//  The canvas beside or beneath the panel, drawn on. app/md-layout.jsx reserved this space
//  from the day it was written — "a place to drop records, connect them, pin a
//  board" — and until now it held one sentence of grey text. This is the board
//  arriving.
//
//  WHAT DRAWS IT. Excalidraw, the real one, MIT-licensed and bundled by
//  scripts/build.mjs. Writing a drawing engine was the alternative and it is
//  months of work before the first arrow snaps to a shape; this is a canvas
//  with freehand, shapes, arrows that bind and follow, text, images, grouping,
//  undo, zoom and export on the first day.
//
//  IT IS NOT LOADED UNTIL IT IS NEEDED. The engine is ~8 MB across 179 chunks
//  and lives outside the service worker's precache, so it is fetched the first
//  time a board actually opens — on a screen wide enough to have one — and the
//  worker keeps it from then on. A phone never downloads a byte of it. That is
//  why this module talks to `window.MD_EXCALIDRAW` rather than importing
//  anything: the build knows the hashed URLs, the source cannot.
//
//  PINNED RECORDS — the thing the paragraph above promised, now built. A record
//  on a board is an EMBEDDABLE element carrying the ref in its `link`, and
//  `renderEmbeddable` draws a real ManDana card in it. The engine asks that prop
//  first and only falls back to an iframe when it returns null, so no iframe is
//  ever created here and nothing is loaded from anywhere.
//
//  Being an element rather than a list beside the scene is what makes the rest
//  free: a pin saves, syncs, moves, resizes, groups, z-orders and undoes exactly
//  like a rectangle, through the storage path that already exists — no new key,
//  no KEY_PATTERN change, no server deploy. And an embeddable is BINDABLE, so an
//  arrow drawn to a pinned record follows it, which is the whole of "bring in
//  records to connect them". See mdBoardPinElement in app/md-board.ts.
//
//  A PIN IS A VIEW OF A RECORD, NEVER A COPY OF ONE. Nothing about the writing
//  is stored on the board — only the ref — so a record edited in its day is
//  edited on every board showing it, and a record deleted anywhere leaves a
//  quiet empty slot rather than a stale ghost of what it used to say. That is
//  the same rule the project model follows: a project owns no records, and now
//  neither does a board.
//
//  WHAT WAS TAKEN OFF IT. The board arrived wearing Excalidraw's own chrome and
//  it read as a second app bolted to the side of this one. What is left is the
//  tools and nothing else: the hamburger, the Databases trigger, the help button
//  and the "to move canvas, hold mouse wheel" hint are all gone (the CSS that
//  removes them is in index.html, under `.md-board-canvas`, so the rules sit
//  where the rest of the app's styling does). The things they reached are not
//  lost — export is still ⇧⌘E, help is still ?, the library is still in the
//  engine — they are simply not furniture any more.
//
//  Two more of its ideas were taken over rather than removed. The PAPER is the
//  theme's: white in light, black in dark, decided in Settings and nowhere
//  else, so the canvas background picker is off. And WHICH BOARD is a card in
//  the day panel rather than a bar over the drawing — the drawing gets the
//  whole rectangle, which is the only thing a drawing ever wants.
// ════════════════════════════════════════════════════════════════════════════
const { useState: useStateB, useEffect: useEffectB, useRef: useRefB, useCallback: useCallbackB } = React;
const { ink: inkB, useMdDismissLayer: useEscapeB, mdRgba: rgbaB, MDI: MDIB } = window;

/* ── loading the engine ──────────────────────────────────────────────────────
   One promise for the whole document, however many boards are opened and closed.
   The stylesheet and the font path go in before the module is imported: the
   fonts are fetched at runtime off EXCALIDRAW_ASSET_PATH, and setting it after
   the engine has read it means every drawing renders in a fallback face. */
let mdEnginePromise = null;

function mdLoadEngine() {
  if (mdEnginePromise) return mdEnginePromise;
  const cfg = window.MD_EXCALIDRAW;
  if (!cfg || !cfg.js) {
    /* Only ever true when src/index.html is opened directly, without the build
       — the URLs carry content hashes that only the build knows. Say which of
       the two situations this is rather than throwing a bare import error. */
    mdEnginePromise = Promise.reject(new Error('unbuilt'));
    return mdEnginePromise;
  }
  mdEnginePromise = (async () => {
    window.EXCALIDRAW_ASSET_PATH = cfg.assetPath || '/';
    if (cfg.css && !document.querySelector(`link[href="${cfg.css}"]`)) {
      await new Promise((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cfg.css;
        // Resolve either way: a missing stylesheet is an ugly board, not a
        // broken one, and blocking the canvas on it would be the worse failure.
        link.onload = resolve;
        link.onerror = resolve;
        document.head.appendChild(link);
      });
    }
    return import(new URL(cfg.js, document.baseURI).href);
  })();
  return mdEnginePromise;
}

/* How many times an open desk quietly tries to read itself again before it
   stops and waits for the person. Three, and the count is refilled every time
   they come back to the tab — enough to ride out a store that is a moment
   behind, few enough that a store which is genuinely flapping cannot become a
   read loop on the main thread. */
const MD_DESK_AUTO_RETRIES = 3;

const MD_COLLAB_COLOURS = ['#5B8DEF', '#9B6FEA', '#E06B8F', '#E28A43', '#35A77A', '#3E9FB4'];
function mdCollabColour(id) {
  let hash = 0;
  for (let i = 0; i < String(id || '').length; i++) hash = ((hash * 31) + id.charCodeAt(i)) >>> 0;
  return MD_COLLAB_COLOURS[hash % MD_COLLAB_COLOURS.length];
}

/* ── which boards there are, and which one this screen is on ─────────────────
   Held by md-app.jsx and handed to both the card in the panel and the canvas,
   because after this change they are in two different places on screen and
   must not be able to disagree about which board is open.

   `enabled` is the canvas. Asking which board is open is what CREATES the
   first one, so a phone calling this would mint a board it has no way to see
   and then sync it. The order inside `refresh` matters for the same reason and
   is the whole point of doing both reads in one function: the id is asked for
   FIRST, because that call is what makes the list non-empty. */
function useBoards(enabled, createIfEmpty = true) {
  const [state, setState] = useStateB({ boards: [], openId: null });
  const refresh = useCallbackB(() => {
    const boards = window.mdBoardIndex();
    let openId = null;
    if (createIfEmpty) openId = window.mdBoardOpenId();
    else {
      const remembered = window.mdRead ? window.mdRead('md:boardOpen', null) : null;
      openId = boards.some((b) => b.id === remembered) ? remembered : (boards[0]?.id || null);
    }
    setState({ openId, boards: createIfEmpty ? window.mdBoardIndex() : boards });
  }, [createIfEmpty]);
  /* THE DOOR, and it is the only thing here that may mint a desk. It used to
     re-fire on `state.openId` going falsy, which was harmless while the only
     way that happened was arriving with nothing — and became the phantom-desk
     loop the moment a PULL could empty the list underneath it. Asked once per
     enabling, so a refresh can report an empty account without the app
     answering by inventing a desk and pushing it back. */
  const asked = useRefB(false);
  useEffectB(() => {
    if (!enabled || asked.current) return;
    asked.current = true;
    if (!state.openId) refresh();
  }, [enabled, refresh]);

  /* RE-READ, and never re-decide. md-board.ts announces every write to the desk
     index, including the one a PULL makes — that is what puts a desk made on
     the laptop onto the iPad without a reload.

     What this must not do is ask which desk is open. mdBoardOpenId() CREATES a
     desk when the list is empty, which is right at the door of the app and
     catastrophic here: a device that pulls an empty index would mint a desk,
     push it, and hand the other device a desk nobody made — the loop Codex
     chased through three rounds of fixes. So the open desk is kept if the
     account still has it and let go of if it does not. A refresh REPORTS. */
  useEffectB(() => {
    if (!enabled) return undefined;
    const reread = () => setState((prev) => {
      const boards = window.mdBoardIndex();
      const remembered = window.mdRead ? window.mdRead('md:boardOpen', null) : null;
      const openId = boards.some((b) => b.id === prev.openId) ? prev.openId
        : (boards.some((b) => b.id === remembered) ? remembered : (boards[0]?.id || null));
      return { boards, openId };
    });
    window.addEventListener('mdboardschanged', reread);
    return () => window.removeEventListener('mdboardschanged', reread);
  }, [enabled]);

  return { boards: state.boards, openId: state.openId, refresh };
}

/* ── the list of boards ──────────────────────────────────────────────────────
   This was a CARD in the day, under the hero: a permanent "BOARD · <name> ⌄"
   row that opened this list. The card is gone — the day column is the day, and
   a switcher for the other half of the screen was the one row in it that was
   not about today. The list moved intact into the hero's ⋯ menu, which is
   already where the app keeps the doors to its other spaces.

   Intact is the word: switching, renaming in place, and deleting are the same
   three interactions on the same rows, and this component is what BoardCard
   drew inside its popover. Only the thing it hangs from changed.

   Deleting is raised to md-app.jsx rather than confirmed here: the app has one
   confirm dialog and it fills the panel, which this list cannot do from inside
   the menu it lives in. */
function BoardPreview({ scene, accent, light, unreadable }) {
  /* THE ONE THING THIS CARD MUST NOT GUESS AT. mdBoardLoad hands the canvas an
     empty scene when it cannot read a desk, because a canvas has to mount — and
     for a shelf that only ever asked "has this anything on it", six drawings
     that were all still there read as six empty desks. It asks md-board.ts
     first now, and says what it was told. */
  if (unreadable) return <div data-md-desk-preview-unreadable="true" style={{ position: 'absolute', inset: 0,
    display: 'grid', placeItems: 'center', color: inkB(.4), fontSize: 10.5, fontWeight: 620,
    letterSpacing: '.02em', textAlign: 'center', padding: '0 8px',
    backgroundColor: light ? '#f7f8fa' : '#0b0c0f',
    backgroundImage: `radial-gradient(circle, ${inkB(.12)} 1px, transparent 1px)`,
    backgroundSize: '13px 13px' }}>CAN&rsquo;T BE READ</div>;

  const visible = window.mdBoardVisibleElements
    ? window.mdBoardVisibleElements(scene?.elements)
    : (Array.isArray(scene?.elements) ? scene.elements.filter((el) => el?.isDeleted !== true) : []);
  const all = visible.filter((el) => Number.isFinite(el.x) && Number.isFinite(el.y));
  if (!all.length) return <div data-md-desk-preview-empty="true" style={{ position: 'absolute', inset: 0,
    display: 'grid', placeItems: 'center', color: inkB(.27), fontSize: 10.5, fontWeight: 620,
    backgroundColor: light ? '#f7f8fa' : '#0b0c0f',
    backgroundImage: `radial-gradient(circle, ${inkB(.12)} 1px, transparent 1px)`,
    backgroundSize: '13px 13px' }}>EMPTY DESK</div>;

  /* A miniature map, not another drawing engine. At gallery size exact hand
     pressure and editor chrome are noise; silhouettes preserve the arrangement
     people recognise while keeping every card static, cheap, and safe. Sample
     very large scenes evenly rather than drawing hundreds of off-screen nodes. */
  const stride = Math.max(1, Math.ceil(all.length / 120));
  const elements = all.filter((_, index) => index % stride === 0).slice(0, 120);
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const el of all) {
    const width = Math.max(1, Math.abs(Number(el.width) || 1));
    const height = Math.max(1, Math.abs(Number(el.height) || 1));
    minX = Math.min(minX, el.x); minY = Math.min(minY, el.y);
    maxX = Math.max(maxX, el.x + width); maxY = Math.max(maxY, el.y + height);
  }
  const width = Math.max(40, maxX - minX), height = Math.max(28, maxY - minY);
  const pad = Math.max(10, Math.max(width, height) * .08);
  const stroke = inkB(light ? .46 : .55), fill = inkB(light ? .08 : .11);
  const mark = rgbaB(accent, light ? .78 : .88);
  const shape = (el, index) => {
    const x = Number(el.x), y = Number(el.y);
    const w = Math.max(1, Math.abs(Number(el.width) || 1));
    const h = Math.max(1, Math.abs(Number(el.height) || 1));
    const rotate = Number(el.angle) ? `rotate(${Number(el.angle) * 180 / Math.PI} ${x + w / 2} ${y + h / 2})` : undefined;
    const common = { key: el.id || index, transform: rotate, opacity: Math.max(.22, Math.min(1, (Number(el.opacity) || 100) / 100)),
      vectorEffect: 'non-scaling-stroke' };
    if (el.type === 'ellipse') return <ellipse {...common} cx={x + w / 2} cy={y + h / 2} rx={w / 2} ry={h / 2}
      fill={fill} stroke={stroke} strokeWidth="1.25" />;
    if (el.type === 'diamond') return <polygon {...common}
      points={`${x + w / 2},${y} ${x + w},${y + h / 2} ${x + w / 2},${y + h} ${x},${y + h / 2}`}
      fill={fill} stroke={stroke} strokeWidth="1.25" />;
    if (['line', 'arrow', 'freedraw', 'draw'].includes(el.type)) {
      const points = Array.isArray(el.points) && el.points.length ? el.points : [[0, 0], [w, h]];
      const d = points.map((point, pointIndex) => `${pointIndex ? 'L' : 'M'}${x + (Number(point?.[0]) || 0)} ${y + (Number(point?.[1]) || 0)}`).join(' ');
      return <path {...common} d={d} fill="none" stroke={stroke} strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />;
    }
    if (el.type === 'text') {
      const lines = Math.max(1, Math.min(3, String(el.text || '').split('\n').length));
      return <g {...common}>{Array.from({ length: lines }, (_, line) => <rect key={line} x={x} y={y + line * Math.max(5, h / lines)}
        width={w * (line === lines - 1 ? .68 : 1)} height={Math.max(2, Math.min(7, h / (lines * 2.6)))} rx="2" fill={stroke} />)}</g>;
    }
    const special = el.type === 'image' || el.type === 'embeddable';
    return <rect {...common} x={x} y={y} width={w} height={h} rx={Math.min(10, w / 7, h / 7)}
      fill={special ? rgbaB(accent, light ? .12 : .18) : fill} stroke={special ? mark : stroke}
      strokeWidth={special ? '1.6' : '1.25'} />;
  };
  return <svg data-md-desk-preview="true" aria-hidden="true" width="100%" height="100%"
    viewBox={`${minX - pad} ${minY - pad} ${width + pad * 2} ${height + pad * 2}`}
    preserveAspectRatio="xMidYMid meet" style={{ display: 'block', background: light ? '#f7f8fa' : '#0b0c0f' }}>
    {elements.map(shape)}
  </svg>;
}

function BoardList({ accent, boards, openId, onOpen, onRename, onDelete, onCreate,
  documentScroll = false }) {
  const [renaming, setRenaming] = useStateB(null);
  const [previews, setPreviews] = useStateB({});
  const light = window.mdIsLight();

  /* The scene AND why it is not one, together, from the same read. mdBoardLoad
     is what writes the answer down, so asking straight after it costs nothing
     and asking at any other moment would be asking about a different read. */
  useEffectB(() => {
    let alive = true;
    Promise.all((boards || []).map(async (board) => {
      try {
        const scene = await window.mdBoardLoad(board.id);
        return [board.id, { scene, why: window.mdBoardUnreadable ? window.mdBoardUnreadable(board.id) : null }];
      } catch (error) { return [board.id, { scene: { elements: [], appState: {} }, why: null }]; }
    })).then((entries) => { if (alive) setPreviews(Object.fromEntries(entries)); });
    return () => { alive = false; };
  }, [boards]);

  /* ASK AGAIN. Marwan's rule for this card: a desk that cannot be read is
     recoverable, never faulty for ever. Two things can be wrong and the order
     here is the cheap one first — md-board.ts drops the database connection
     and re-reads locally, and only if that is not the trouble is the account
     asked for bytes this device has not got. One pull, then one more look; a
     retry that finds nothing is a retry, not a loop. */
  const retry = useCallbackB(async (id) => {
    if (typeof window.mdBoardRetry !== 'function') return;
    let why = await window.mdBoardRetry(id);
    if (why !== 'ok' && typeof window.mdSync === 'function') {
      try { await window.mdSync(); } catch (error) { /* offline: the local answer stands */ }
      why = await window.mdBoardRetry(id);
    }
    const scene = await window.mdBoardLoad(id);
    setPreviews((p) => ({ ...p, [id]: { scene, why: why === 'ok' ? null : why } }));
  }, []);

  /* WHERE EACH DESK IS (5.2), read ONCE for the whole list. mdSyncStates walks
     all of storage, so asking it per card is how a shelf of forty desks turns
     into a stutter — the shape md-librarian.ts landed on for the same
     question.

     THE STORAGE KEY IS THE INDEX, NOT THE DRAWING, and that is not a shortcut.
     A desk's scene lives in IndexedDB and is offered asynchronously
     (mdBoardSyncDocs), so it is absent from mdSyncStates by construction and
     `md:board:<id>` would read 'local' for every desk on a fully synced
     account — a confident lie. `md:boards` is the document a desk's row
     actually travels in, and it is the same answer md-record-index.ts:229
     already gives when asked where a board is kept.

     THE DRAWING IS SPOKEN FOR SEPARATELY, as of 3.2, and by a question with no
     I/O in it: md-board.ts names every desk document it writes, so the card
     asks whether this desk is in that set rather than trying to read it. That
     is the honesty this mark was missing — a desk drawn on and never pushed
     used to look exactly like one that had been on the server for a week.

     What IS per-desk is the second half, and it is the half this story exists
     for: `board:<id>` is the reference md-board.ts:365 records a hold under,
     so a drawing or a pasted image still in the upload queue names this desk
     and this desk only. */
  const snap = window.mdWhereSnapshot ? window.mdWhereSnapshot() : null;
  const Mark = window.MdWhereMark;
  /* AND THE ROW IS ASKED ABOUT PER ROW. Reading `md:boards` for every card was
     honest about the document and wrong about the desks: renaming one desk lit
     the mark on all four, because they all asked the same question about the
     one list they all travel in. The list merges per row now and each row
     carries `edited`, so the two facts that separate them are here — where the
     whole document stands, and when it was last agreed with the server. Both
     are read ONCE for the shelf; mdBoardRowState is pure and does the rest. */
  const indexState = window.mdSyncStateOf && snap
    ? window.mdSyncStateOf('md:boards', snap.states) : null;
  const indexSeenAt = window.mdSyncSeenAt ? window.mdSyncSeenAt('boards') : 0;

  const card = (b) => {
    const selected = b.id === openId;
    const why = previews[b.id] && previews[b.id].why;
    return <article key={b.id} data-md-desk-card="true" style={{ position: 'relative', minWidth: 0,
      boxSizing: 'border-box',
      borderRadius: 15, overflow: 'hidden', border: `1px solid ${selected ? rgbaB(accent, .68) : inkB(.09)}`,
      background: light ? '#fff' : '#111318', boxShadow: selected ? `0 0 0 2px ${rgbaB(accent, .12)}` : 'none' }}>
      <button onClick={() => onOpen(b.id)} aria-label={`Open ${b.name}`}
        aria-current={selected ? 'true' : undefined}
        style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: 0, border: 0,
          background: 'transparent', color: inkB(.88), cursor: 'pointer', textAlign: 'start' }}>
        <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 10', overflow: 'hidden',
          borderBottom: `1px solid ${inkB(.07)}` }}>
          <BoardPreview scene={previews[b.id] && previews[b.id].scene} unreadable={why} accent={accent} light={light} />
          {selected && <span aria-hidden="true" style={{ position: 'absolute', top: 8, insetInlineStart: 8,
            width: 7, height: 7, borderRadius: '50%', background: rgbaB(accent, 1), boxShadow: `0 0 0 3px ${rgbaB(accent, .18)}` }} />}
        </div>
        <div dir="auto" data-md-desk-title="true" style={{ position: 'relative', width: '100%',
          height: 42, minHeight: 42, flex: '0 0 42px', boxSizing: 'border-box', display: 'flex', alignItems: 'center',
          paddingInlineStart: 10, paddingInlineEnd: 66, fontSize: 12.5, fontWeight: selected ? 680 : 570,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.name}</div>
      </button>
      {/* OUTSIDE the open button. A button inside a button is not a control —
          and what the outer one does is open a desk that cannot be read. The
          overlay mirrors the preview's own aspect ratio so it sits under the
          sentence at every card width, and passes through every press that is
          not aimed at it. */}
      {why && <div style={{ position: 'absolute', insetInline: 0, top: 0, aspectRatio: '16 / 10',
        display: 'grid', alignContent: 'end', justifyItems: 'center', paddingBottom: 12,
        pointerEvents: 'none' }}>
        <button data-md-desk-retry="true" onClick={() => retry(b.id)} aria-label={`Try ${b.name} again`}
          title="Try reading this desk again"
          style={{ pointerEvents: 'auto', padding: '4px 12px', borderRadius: 999, cursor: 'pointer',
            border: `1px solid ${rgbaB(accent, .5)}`, background: light ? '#fff' : '#111318',
            color: inkB(.78), font: 'inherit', fontSize: 10.5, fontWeight: 680, letterSpacing: '.04em' }}>RETRY</button>
      </div>}
      {/* Outside the button, mirroring the open-desk dot on the other corner.
          It is a fact about the desk rather than something to press, and a
          fact inside a control is a thing people try to click. */}
      {Mark && snap && <Mark
        where={window.mdWhereRead({ refs: [`board:${b.id}`],
          doc: [
            /* WHERE THIS DESK'S ROW STANDS, which is not where the list
               stands — see indexState above. */
            window.mdBoardRowState
              ? window.mdBoardRowState(b, indexState, indexSeenAt) : indexState,
            /* AND THE DRAWING ITSELF, which this mark could not speak about
               until 3.2 — see the comment above `snap`. It is a set membership
               test, no database and no hashing, so it costs the same whether
               the shelf holds two desks or two hundred. `null` when nothing is
               known, and mdWhereRead drops those: the mark then says exactly
               what it said before rather than inventing agreement it never
               checked. */
            window.mdSyncTouchedState ? window.mdSyncTouchedState(`board:${b.id}`) : null,
          ] },
        snap)}
        style={{ position: 'absolute', top: 9, insetInlineEnd: 9, zIndex: 1 }} />}
      {renaming === b.id ? (
        <input autoFocus defaultValue={b.name} aria-label={`Rename ${b.name}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { onRename(b.id, e.currentTarget.value); setRenaming(null); }
            if (e.key === 'Escape') { e.stopPropagation(); setRenaming(null); }
          }}
          onBlur={(e) => { onRename(b.id, e.currentTarget.value); setRenaming(null); }}
          style={{ position: 'absolute', zIndex: 2, insetInline: 6, bottom: 5, height: 32, boxSizing: 'border-box',
            minWidth: 0, padding: '5px 8px', borderRadius: 8, border: `1px solid ${rgbaB(accent, 0.5)}`,
            background: light ? '#fff' : '#111318', color: inkB(0.9), font: 'inherit', fontSize: 12.5 }} />
      ) : (
        <div style={{ position: 'absolute', insetInlineEnd: 5, bottom: 7, display: 'flex', gap: 1 }}>
          <button aria-label={`Rename ${b.name}`} onClick={() => setRenaming(b.id)} title="Rename"
            style={iconBtn()}>{MDIB.edit(inkB(0.42), 14)}</button>
          <button aria-label={`Delete ${b.name}`} onClick={() => onDelete(b)} title="Delete"
            style={iconBtn()}>{MDIB.close(inkB(0.42), 14)}</button>
        </div>
      )}
    </article>;
  };

  return <div data-md-desk-grid="true" data-md-phone-document-content={documentScroll ? 'true' : undefined}
    /* AUTO-FILLED TRACKS, NOT A FIXED COUNT. Every card used to declare
       `calc(50% - 5px)`, which is a claim about the screen made in the one
       place that cannot see it: two desks wide on a 340px phone and two desks
       wide on a 1000px window, with the iPad's four cards using a quarter of
       the screen and seven hundred points of empty column under them.

       160px is the only number in the rule and it is the whole rule: the
       narrowest a card can be and still read as a desk. It gives two on a
       phone and in the day column, and as many as fit on anything wider —
       without a breakpoint to keep in step with md-layout.jsx. `start` on both
       axes because the rows belong at the top of a tall column; stretch would
       make four desks into four half-screen-high cards. */
    className="md-scroll" style={{ display: 'grid', flex: 1, minHeight: 0,
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(160px, 100%), 1fr))',
    gap: 10, alignItems: 'start', alignContent: 'start',
    overflowY: 'auto', padding: 2 }}>
    {(boards || []).map(card)}
    {onCreate && <article data-md-new-desk-card="true" style={{ position: 'relative', minWidth: 0,
      boxSizing: 'border-box',
      borderRadius: 15, overflow: 'hidden',
      border: `1px dashed ${inkB(.16)}`, background: inkB(.025) }}>
      <button aria-label="New desk" onClick={onCreate} style={{ display: 'flex', flexDirection: 'column',
        width: '100%', padding: 0, border: 0, background: 'transparent', color: inkB(.52), cursor: 'pointer', textAlign: 'start' }}>
        <div style={{ width: '100%', aspectRatio: '16 / 10', display: 'grid', placeItems: 'center',
          backgroundImage: `radial-gradient(circle, ${inkB(.1)} 1px, transparent 1px)`, backgroundSize: '13px 13px' }}>
          <span aria-hidden="true" style={{ width: 38, height: 38, display: 'grid', placeItems: 'center',
            borderRadius: 12, background: rgbaB(accent, .12), color: rgbaB(accent, 1), fontSize: 26, lineHeight: 1 }}>+</span>
        </div>
        <div data-md-desk-title="true" style={{ position: 'relative', width: '100%',
          height: 42, minHeight: 42, flex: '0 0 42px', display: 'flex', alignItems: 'center', padding: '0 10px',
          boxSizing: 'border-box', fontSize: 12.5, fontWeight: 620 }}>New Desk</div>
      </button>
    </article>}
  </div>;
}

function iconBtn() {
  return {
    width: 26, height: 26, flexShrink: 0, display: 'grid', placeItems: 'center',
    borderRadius: 7, border: 'none', background: 'transparent',
    color: window.ink(0.42), fontSize: 12, cursor: 'pointer',
    fontFamily: 'var(--md-body)',
  };
}

/* What a resolved record shows on a board, or null for an ordinary card. One
   helper because three places ask it — the size a pin wants, what to draw in it,
   and what the card menu can offer — and three answers to "is this record a
   video" is how a board comes to draw one thing and measure another.

   mdEmbedLegacy is the fold for records written before a document lived in the
   writing; see md-embed.ts. */
function mdBoardEmbed(found) {
  if (!found || !window.mdEmbedScan) return null;
  const text = window.mdEmbedLegacy
    ? window.mdEmbedLegacy(found.text, found.file) : found.text;
  return mdBoardFirst(window.mdEmbedScan(text));
}
function mdBoardFirst(list) { return list && list.length ? list[0] : null; }

/* ── a record, pinned ────────────────────────────────────────────────────────
   Drawn inside the box the element owns, so it fills its width and height and
   the engine keeps the geometry. The grammar is the day card's, unchanged: the
   40px tile carrying the hue at 0.16 inside a 0.75px 0.34 edge, the title at
   16/600 on full ink, the meta at 12.5 on 0.6.

   The surface is NOT <Glass>. Glass is a frosted pane over the app's backdrop,
   and here what is behind is a drawing — blurring somebody's strokes into a
   smear under every pinned card is the one place the material stops being right.
   An opaque paper-coloured card with the same corner and hairline is what a
   piece of paper on a whiteboard actually looks like. */
function PinnedRecord({ el, ctx, light, onOpen, onManifestRead, onAsk, scopeRef, stagedInstance, stoppedInstances }) {
  const found = window.mdRecordResolve(window.mdBoardLinkRef(el.link), ctx);
  const paper = light ? 'rgba(255,255,255,0.97)' : 'rgba(26,28,36,0.97)';
  const edge = inkB(light ? 0.1 : 0.16);

  /* A ref outliving what it names is ordinary, not an error: the record was
     deleted, or lives on a device that has not synced yet. So the slot stays —
     the space on the board is the user's own arrangement and deleting it for
     them would be the wrong repair — and it says which of the two it is
     without shouting. */
  if (!found) {
    return (
      <div style={{ width: '100%', height: '100%', boxSizing: 'border-box', borderRadius: 20,
        display: 'grid', placeItems: 'center', padding: 12,
        border: `1px dashed ${inkB(light ? 0.14 : 0.2)}`, background: 'transparent',
        color: inkB(0.32), fontSize: 12.5, fontWeight: 550, textAlign: 'center',
        fontFamily: 'var(--md-body)' }}>
        This record is no longer here
      </div>);
  }

  /* A record whose writing is a link IS that thing here. Nothing about the
     record changes — the embed is read out of its own text (see md-embed.ts), so
     a link typed into an ordinary record on an ordinary day becomes a player the
     moment it is pinned, and taking the line out again turns it back into a
     card. No flag to keep in step with the writing.

     The FIRST one, when a record holds several: a pinned card is one rectangle,
     and the record's own order is the only non-arbitrary way to choose. Opening
     the record shows all of them. */
  /* A manifestation is not a record with an embed in its writing — it has no
     writing at all — so it is answered before the embed grammar is consulted.
     md-resolve hands back the library row on `manifest`, and that row is
     everything needed to draw one without its bytes. */
  const foundCraft = found.craft || found.manifest;
  if (foundCraft && window.CraftFrame) {
    /* Its frame is on the stage. Showing the name rather than an empty box, so
       the space on the board still says what is in it. */
    if (stagedInstance && stagedInstance === foundCraft.instance) {
      return (
        <div style={{ width: '100%', height: '100%', boxSizing: 'border-box', borderRadius: 20,
          display: 'grid', placeItems: 'center', padding: 12, gap: 6, textAlign: 'center',
          border: `1px dashed ${inkB(light ? 0.14 : 0.2)}`, fontFamily: 'var(--md-body)' }}>
          <span style={{ color: inkB(0.7), fontSize: 13.5, fontWeight: 600 }}>
            {foundCraft.name}
          </span>
          <span style={{ color: inkB(0.32), fontSize: 12, fontWeight: 550 }}>Open full screen</span>
        </div>);
    }
    return <window.CraftFrame key={foundCraft.instance} entry={foundCraft}
      light={light} title={found.title} onRead={onManifestRead} onAsk={onAsk}
      surface="board" scopeRef={scopeRef}
      stopped={stoppedInstances && stoppedInstances.has(foundCraft.instance)} />;
  }

  const embed = mdBoardEmbed(found);
  if (embed) {
    return <window.EmbedView key={embed.key} embed={embed} light={light} title={found.title} />;
  }

  const col = found.domainId ? window.mdDomainColor(found.domainId, light) : inkB(0.55);
  const icon = found.domainId ? window.mdDomainIcon(found.domainId, col, 18) : null;

  return (
    <button onClick={() => onOpen && onOpen(found)}
      aria-label={`Open “${found.title}”`}
      style={{ width: '100%', height: '100%', boxSizing: 'border-box', display: 'block',
        textAlign: 'start', cursor: 'pointer', padding: '13px 14px', borderRadius: 20,
        background: paper, border: `0.75px solid ${edge}`, fontFamily: 'var(--md-body)',
        boxShadow: light ? '0 6px 18px rgba(40,60,110,0.10)' : '0 8px 22px rgba(0,0,0,0.42)',
        overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{ width: 40, height: 40, borderRadius: 13, flexShrink: 0,
          display: 'grid', placeItems: 'center',
          background: rgbaB(col, light ? 0.12 : 0.16),
          border: `0.75px solid ${rgbaB(col, light ? 0.28 : 0.34)}` }}>{icon}</span>
        <span style={{ flex: 1, minWidth: 0, display: 'block' }}>
          <span style={{ display: 'block', color: inkB(1), fontSize: 16, fontWeight: 600,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{found.title}</span>
          <span style={{ display: 'block', color: inkB(0.6), fontSize: 12.5, marginTop: 5,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {found.day || (found.type === 'project' ? 'Project' : found.type === 'domain' ? 'Domain' : '')}
          </span>
        </span>
      </div>
    </button>);
}

/* An account-owned destination dropped from Tabs. It is deliberately not a
   record card: the board element stores the stable navigation target itself,
   resolves its title and preview live, and opens that exact native destination.
   This is the small-frame counterpart of the richer Favorite preview. */
function PinnedDestination({ el, ctx, light, accent }) {
  const target = window.mdBoardLinkNavigation?.(el.link);
  const row = target && window.mdFindTargetRow?.(target, ctx);
  const Glyph = window.MdFindGlyph;
  const paper = light ? '#fff' : '#1a1c24';
  const edge = inkB(light ? 0.12 : 0.18);
  if (!target || !row) return <div data-md-navigation-frame="missing" style={{ width: '100%', height: '100%',
    boxSizing: 'border-box', display: 'grid', placeItems: 'center', borderRadius: 20,
    border: `1px dashed ${edge}`, color: inkB(.34), fontSize: 13, fontFamily: 'var(--md-body)' }}>
    This destination is no longer here
  </div>;
  const meta = [row.project && `Project · ${row.project}`, row.database && `Database · ${row.database}`,
    row.part && `Part of Life · ${row.part}`].filter(Boolean).join('  ·  ');
  return <button data-md-navigation-frame={row.category || 'Destination'}
    aria-label={`Open “${row.title}”`} onClick={() => window.mdNavigationNavigate?.(target, { source: 'desk' })}
    style={{ width: '100%', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
      padding: 0, overflow: 'hidden', textAlign: 'start', cursor: 'pointer', borderRadius: 20,
      border: `1px solid ${edge}`, background: paper, color: inkB(.94), fontFamily: 'var(--md-body)',
      boxShadow: light ? '0 12px 30px rgba(40,60,110,.12)' : '0 14px 34px rgba(0,0,0,.46)' }}>
    <span style={{ height: 38, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8,
      padding: '0 13px', borderBottom: `1px solid ${inkB(light ? .08 : .11)}`,
      color: inkB(.48), fontSize: 11.5, fontWeight: 650 }}>
      {Glyph ? <Glyph go={row.place?.go} category={row.category} color={rgbaB(accent, .9)} /> : <span aria-hidden="true">□</span>}
      <span>{row.category}</span><span aria-hidden="true" style={{ marginInlineStart: 'auto' }}>↗</span>
    </span>
    <span style={{ flex: 1, minHeight: 0, display: 'block', padding: '21px 22px 16px' }}>
      <strong dir="auto" style={{ display: 'block', fontSize: 21, lineHeight: 1.2, fontWeight: 690,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title || 'Untitled'}</strong>
      <span dir="auto" style={{ display: '-webkit-box', marginTop: 13, color: inkB(.55), fontSize: 13.5,
        lineHeight: 1.55, WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
        {row.snippet || 'Open this frame to see the complete destination.'}
      </span>
      {meta && <small dir="auto" style={{ display: 'block', marginTop: 14, color: inkB(.34), fontSize: 11,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meta}</small>}
    </span>
  </button>;
}

/* ── a record that is a video, or a picture ──────────────────────────────────
   Fills the element's box edge to edge with the same 20px corner every card in
   the app has, and nothing else. No title bar over it: the box is the picture,
   and a strip of our chrome across a video would be the one thing on this board
   pretending to be a window rather than a thing on a table.

   THE FRAME IS OURS, AND THE ENGINE'S EMBED ALLOWLIST IS NEVER CONSULTED.
   Excalidraw can frame third-party hosts by itself, and none of that machinery
   is reachable from here: the element's `link` is a ManDana ref and nothing
   else, `validateEmbeddable` still answers only to those, and the media URL
   lives inside the record's text where the engine never looks. So every
   attribute below — what the frame may do, where it may navigate, what referrer
   it leaks — is decided in this file rather than by a library's defaults.

   `sandbox` is the one that earns its keep. Without it a framed page can
   navigate the WHOLE TAB away from the app; the default sandbox forbids that,
   and the four capabilities added back are the ones a video player genuinely
   needs. `allow-same-origin` sounds alarming and is not: it is same-origin with
   respect to youtube-nocookie.com, not to us, and dropping it stops the player
   loading at all. */
/* ── the canvas ────────────────────────────────────────────────────────────── */
function BoardCanvas({ accent, light, openId, registerApi, ctx, onOpenRecord, onManifestRead, onAddMedia, onAddFile, onDeleteRecord, onContent, muted = false, active = true, chromeStart = 0 }) {
  const scopeRef = openId && window.mdBoardScope ? window.mdBoardScope(openId) : null;
  const [engine, setEngine] = useStateB(null);
  const [failed, setFailed] = useStateB(null);
  const [notice, setNotice] = useStateB(null);
  /* What this desk still owes the store (1.18). Read rather than pushed: the
     blob queue has no change event, and giving it one would mean every surface
     that ever draws a transfer registering a listener the door has to remember
     to fire. A poll that runs only while something is in flight costs nothing
     when nothing is — which is almost always. */
  const [transfers, setTransfers] = useStateB([]);
  /* Shut on arrival, and it stays however it was left for as long as the app is
     open — including across a switch of boards, because a preference about
     drawing is about the person drawing and not about which board they are on.
     It is not persisted past a reload: a fresh session of a calm app opens
     calm, and re-opening it is one click. */
  const [styleOpen, setStyleOpen] = useStateB(false);
  /* Excalidraw owns and may rebuild the toolbar row, so Style is rendered
     through a portal into a tiny host that can be moved back into the live row
     without remounting the button or losing its open state. */
  const [styleHost, setStyleHost] = useStateB(null);
  /* And the zoom row's, at the head of the footer row. Reset used to have a
     portal host of its own seated there; it moved into the toolbar's ⋯ menu,
     so the only hosts left are Style's and the zoom row's. */
  const [zoomHost, setZoomHost] = useStateB(null);
  /* How far out the board is, as the engine reports it. Held in state because
     the readout has to follow a pinch, and a pinch re-renders nothing here. */
  const [zoomPct, setZoomPct] = useStateB(100);
  /* The one selected card, in CONTAINER pixels, or null. Held in state rather
     than read when needed, because the ⋯ has to stay on the card through a
     drag, a resize, a pan and a zoom — every one of those is an appState change
     the engine reports, and none of them re-renders this component by itself. */
  const [card, setCard] = useStateB(null);
  const [cardMenu, setCardMenu] = useStateB(false);
  const [hasBoardContent, setHasBoardContent] = useStateB(false);
  const [collabPeers, setCollabPeers] = useStateB([]);
  const [collabState, setCollabState] = useStateB('closed');
  const [collabEnabled, setCollabEnabled] = useStateB(() =>
    typeof window.mdCollabEnabled === 'function' && window.mdCollabEnabled(openId));
  const [stoppedInstances, setStoppedInstances] = useStateB(() => new Set());
  const apiRef = useRefB(null);
  const collabRef = useRefB(null);
  const elementVersionsRef = useRefB(new Map());
  const remoteVersionsRef = useRefB(new Map());
  const lastChangedRef = useRefB([]);
  const drawingFreehandRef = useRefB(false);
  /* fileIds this session has already announced, so a peer's image reconciled
     into the scene does not echo straight back as if it were drawn here. */
  const publishedAssetsRef = useRefB(new Set());
  const saveTimer = useRefB(null);
  /* What the last write put on disk, as mdBoardSceneStamp reads it — the
     memory the guard in `save` compares against. Seeded from the stored scene
     when a desk opens, because the engine's first onChange arrives before
     anybody has touched anything. */
  const savedStamp = useRefB(null);

  /* One quiet remote apply for both collaboration lanes. A desk-room frame and
     a durable pull differ in how they arrived, not in how Excalidraw should
     receive them: reconcile against unsaved local work, never enter local
     Undo, and mark only versions that actually won so onChange cannot echo
     them back into a live room. */
  const applyRemoteScene = useCallbackB((remote) => {
    const api = apiRef.current;
    if (!api || !Array.isArray(remote) || !engine || !engine.reconcileElements) return [];
    const local = api.getSceneElementsIncludingDeleted
      ? api.getSceneElementsIncludingDeleted()
      : api.getSceneElements();
    const merged = engine.reconcileElements(local, remote, api.getAppState());
    const accepted = new Map(merged.map((el) => [el.id, elementVersion(el)]));
    for (const el of remote) {
      const version = elementVersion(el);
      if (accepted.get(el.id) === version) remoteVersionsRef.current.set(el.id, version);
      if (el && el.type === 'image' && el.fileId) publishedAssetsRef.current.add(el.fileId);
    }
    api.updateScene({ elements: merged,
      captureUpdate: engine.CaptureUpdateAction && engine.CaptureUpdateAction.NEVER });
    return merged;
  }, [engine]);

  /* Only the rows this desk owns. A ref on a queue row is the record that owns
     the bytes (0.7), so somebody else's picture going up for another desk is
     not this desk's business to report. */
  const readTransfers = useCallbackB(async () => {
    if (typeof window.mdBlobTransfers !== 'function') return;
    const mine = `board:${openId}`;
    let rows = [];
    try { rows = await window.mdBlobTransfers(); } catch (err) { rows = []; }
    setTransfers((rows || []).filter((row) =>
      Array.isArray(row.refs) && row.refs.includes(mine)));
  }, [openId]);

  useEffectB(() => {
    if (!openId) { setTransfers([]); return undefined; }
    readTransfers();
    /* Two seconds. Fast enough that a bar moves while somebody watches it,
       slow enough that a desk left open on a phone is not reading a database
       every frame for the rest of the afternoon. Stopped the moment there is
       nothing to watch, and restarted by the save that put something there. */
    const tick = setInterval(readTransfers, 2000);
    return () => clearInterval(tick);
  }, [openId, readTransfers]);

  const onTransferAct = useCallbackB(async (what, hash) => {
    const door = { pause: window.mdBlobPause, resume: window.mdBlobResume,
      cancel: window.mdBlobCancel }[what];
    if (typeof door !== 'function') return;
    const out = await door(hash, `board:${openId}`);
    /* A cancelled file must stop being named by the desk that named it, or the
       record points at bytes that will never exist — the unresolvable
       reference 0.9 is about, arrived at from the other direction. The door
       says which reference to drop; taking it off the scene is the surface's
       job because the door does not know what a desk is. */
    if (what === 'cancel' && out && out.ok && window.mdBoardForgetFile) {
      await window.mdBoardForgetFile(openId, out.unname && out.unname.hash);
    }
    readTransfers();
  }, [openId, readTransfers]);
  const styleHostRef = useRefB(null);
  const zoomHostRef = useRefB(null);
  /* The zoom this board last ASKED the engine for, until the engine says it
     has it. See zoomBy — it is what makes two taps in one tick two steps. */
  const zoomPendingRef = useRefB(null);
  /* What the person has already read and closed. Every save re-decides what the
     banner should say, and a save runs whenever the engine reports a change —
     including the change of the banner's own appearance. Without this, closing
     "too big to sync" reopened it a moment later, forever: the dismissal WAS
     the next change. So a dismissed sentence stays dismissed, and only a save
     that says something different is allowed to speak again. */
  const dismissed = useRefB(null);
  const wrapRef = useRefB(null);
  /* THE WRAPPER IS WATCHED AS STATE, NOT ONLY HELD AS A REF, because the
     measuring effect below has to run again when the element it measures is a
     DIFFERENT element. A ref changing is invisible to React: the effect's deps
     are the engine and the open desk, and neither of them moves when the board
     is unmounted and remounted — which is exactly what happens when the window
     crosses the phone breakpoint, where there is no board at all, and comes
     back. The old wrapper's observers went with it, the new one had none, and
     Style, the ⋯ and Reset were gone until a reload. So the ref is a callback
     that also records the node, and the effect depends on the node. */
  const [wrapEl, setWrapEl] = useStateB(null);
  const setWrap = useCallbackB((node) => { wrapRef.current = node; setWrapEl(node); }, []);
  const maskRef = useRefB(null);

  const elementVersion = (el) => el
    ? `${el.id}:${Number(el.version) || 0}:${Number(el.versionNonce) || 0}:${el.isDeleted ? 1 : 0}`
    : '';

  /* What rectangle a ref wants. Asked before the element is built rather than
     after, because the pin is CENTRED on its point and centring needs the size —
     getting this second would put a video 110px off centre.

     `known` is the media the caller ALREADY has in its hand, and it is not an
     optimisation. A dropped link creates its record through a React state
     update, and the pin is built in the same tick — so `ctx` here is still the
     render's old value and cannot contain the record that was just made. Asking
     it produced a confident "not media" and a 260×92 slot with a video crushed
     into it. The picker path has no such problem, because there the record
     existed long before the pin, so it resolves as it always did. */
  const sizeFor = useCallbackB((ref, known) => {
    /* Read off the ref's own TYPE rather than by resolving it. A manifestation
       is the one kind of pin whose size does not depend on anything we would
       have to look up, and the same-tick problem the `known` argument exists for
       applies here too — a drop mints the portal before any render has seen the
       library grow. */
    if (ref && ref.type === window.MD_CRAFT_TYPE) {
      return { width: window.MD_BOARD_MANIFEST_W, height: window.MD_BOARD_MANIFEST_H };
    }
    const embed = known || mdBoardEmbed(
      window.mdRecordResolve && window.mdRecordResolve(ref, ctx));
    if (!embed) return { width: window.MD_BOARD_PIN_W, height: window.MD_BOARD_PIN_H };
    if (embed.kind === 'file') return { width: window.MD_BOARD_FILE_W, height: window.MD_BOARD_FILE_H };
    if (embed.kind === 'link') return { width: window.MD_BOARD_PIN_W, height: window.MD_BOARD_PIN_H };
    return embed.kind === 'image'
      ? { width: window.MD_BOARD_IMAGE_W, height: window.MD_BOARD_IMAGE_H }
      : { width: window.MD_BOARD_MEDIA_W, height: window.MD_BOARD_MEDIA_H };
  }, [ctx]);

  /* Put a pinned record in the middle of what is currently on screen, rather
     than at the scene origin — which on a board that has been scrolled is
     somewhere the user cannot see, so the pin would land silently nowhere.
     Excalidraw's own mapping: sceneX = viewportX / zoom - scrollX.

     `at` is the scene point a DROP asks for. A picker has no position and gets
     the viewport centre; a drop does, and landing somewhere other than where the
     file was let go is the one thing a drop must never do. */
  const pin = useCallbackB((ref, at, known) => {
    const api = apiRef.current;
    if (!api) return;
    const st = api.getAppState();
    const z = (st.zoom && st.zoom.value) || 1;
    const existing = api.getSceneElements();
    const size = sizeFor(ref, known);
    let x = at ? at.x - size.width / 2 : st.width / 2 / z - st.scrollX - size.width / 2;
    let y = at ? at.y - size.height / 2 : st.height / 2 / z - st.scrollY - size.height / 2;
    /* Cascade off whatever is already there. Two pins dropped in a row both
       land on the centre of the same viewport, so the second covers the first
       exactly — which does not look like two cards, it looks like the pin
       silently failing. Stepping down and across is what every window manager
       does with a new window, and for this reason.

       A drop is exempt: it landed where somebody put it, and nudging it 26px
       away from a card that happens to be there would be the app arguing with
       a gesture that was already precise. */
    const taken = (px, py) => existing.some((e) => e && !e.isDeleted
      && Math.abs(e.x - px) < 12 && Math.abs(e.y - py) < 12);
    if (!at) for (let i = 0; i < 40 && taken(x, y); i++) { x += 26; y += 26; }
    const el = window.mdBoardPinElement(ref, x, y, size);
    if (!el) return;
    api.updateScene({ elements: [...existing, el] });
  }, [sizeFor]);

  const pinNavigation = useCallbackB((target, at) => {
    const api = apiRef.current;
    const link = window.mdBoardNavigationLink?.(target);
    if (!api || !link) return false;
    const st = api.getAppState();
    const z = (st.zoom && st.zoom.value) || 1;
    const existing = api.getSceneElements();
    const size = { width: window.MD_BOARD_NAVIGATION_W, height: window.MD_BOARD_NAVIGATION_H };
    let x = at ? at.x - size.width / 2 : st.width / 2 / z - st.scrollX - size.width / 2;
    let y = at ? at.y - size.height / 2 : st.height / 2 / z - st.scrollY - size.height / 2;
    const taken = (px, py) => existing.some((e) => e && !e.isDeleted
      && Math.abs(e.x - px) < 12 && Math.abs(e.y - py) < 12);
    if (!at) for (let i = 0; i < 40 && taken(x, y); i++) { x += 26; y += 26; }
    const el = window.mdBoardNavigationElement?.(target, x, y, size);
    if (!el) return false;
    api.updateScene({ elements: [...existing, el] });
    return true;
  }, []);

  /* Clear the scene as one ordinary local edit. `resetScene()` also resets the
     history, which would make the wipe permanent; an immediate captured update
     gives Excalidraw's own Undo the full pre-wipe scene instead. Board files are
     deliberately retained because Undo may need them to restore images. */
  const wipeBoard = useCallbackB(() => {
    const api = apiRef.current;
    const capture = engine && engine.CaptureUpdateAction;
    if (!api || !capture || !engine.newElementWith || !api.getSceneElements().length) return;
    const all = api.getSceneElementsIncludingDeleted
      ? api.getSceneElementsIncludingDeleted()
      : api.getSceneElements();
    /* Emptying the array is a hard local replacement: there is no element left
       for onChange to announce. Mark every live element deleted instead. The
       canvas looks equally empty, but the tombstones can travel, survive a
       reconnect snapshot, and be undone as one captured update. */
    const wiped = all.map((el) => el.isDeleted
      ? el : engine.newElementWith(el, { isDeleted: true }));
    api.updateScene({ elements: wiped, captureUpdate: capture.IMMEDIATELY });
  }, [engine]);

  /* The stored scene and its files, read back from IndexedDB before the engine
     mounts. This used to be a synchronous localStorage read during render; the
     store moved (see md-boarddb.js), and an asynchronous read cannot be done
     mid-render — so the engine waits for `boot` and `initialData` is handed
     only what was actually stored. Gating the mount on the read is also the
     race guard: there is no moment where the canvas shows an empty scene for a
     board that has one, which is the failure that turns into a lost drawing
     when the next save lands. */
  const [boot, setBoot] = useStateB(null);          // { id, scene, files, unreadable } | null
  /* ONE WAY IN. A desk that could not be read is read again by bumping this,
     never by dropping a scene in beside the boot state — the element versions,
     the published-asset set and the reset control are all seeded by the code
     below, and a second seeding path is a second place for them to drift. */
  const [reboot, setReboot] = useStateB(0);
  useEffectB(() => {
    if (!openId) return undefined;
    let alive = true;
    setBoot(null);
    (async () => {
      const scene = await window.mdBoardLoad(openId);
      const files = await window.mdBoardFilesFor(openId);
      if (!alive) return;
      elementVersionsRef.current = new Map((scene.elements || []).map((el) => [el.id, elementVersion(el)]));
      remoteVersionsRef.current.clear();
      lastChangedRef.current = [];
      drawingFreehandRef.current = false;
      /* Images already on the desk are not ours to announce — only ones drawn
         or pasted during this session are. */
      publishedAssetsRef.current = new Set((scene.elements || [])
        .filter((el) => el && el.type === 'image' && el.fileId)
        .map((el) => el.fileId));
      /* What is on disk is what was just read off it, so the first onChange
         the engine fires on mount has nothing to report. Without this every
         desk would write itself once simply for having been opened.

         A RE-READ SEEDS IT TOO, and that is the useful half on this path. The
         engine does not remount when `reboot` bumps, so a desk that failed to
         read and then read cleanly keeps whatever is on the canvas — and this
         line makes the stamp disagree with it, which is exactly what turns the
         next onChange back into a save. */
      savedStamp.current = window.mdBoardSceneStamp(scene, files);
      /* WHY THE SCENE ABOVE MAY BE A LIE, carried out of the read that knows.
         mdBoardLoad hands back an empty scene when it could not read the desk,
         because the engine has to mount — and this is the only moment at which
         the difference is still available to ask about. */
      setBoot({ id: openId, scene, files,
        unreadable: window.mdBoardUnreadable ? window.mdBoardUnreadable(openId) : null });
      /* The reset control is useful only while there is something to reset;
         seeded here from what was stored, and from then on Excalidraw's
         synchronous onChange keeps it true through drawing and false through
         a wipe. Undo reports the restored elements through the same path. */
      setHasBoardContent(!window.mdBoardSceneEmpty(scene.elements));
    })();
    return () => { alive = false; };
  }, [openId, reboot]);

  /* Live Sync being off must change immediacy, not convergence. md-sync names
     the documents a pull actually applied; when this open desk is among them,
     repaint it from IndexedDB immediately instead of waiting for a switch or
     reload. A board-file-only pull hydrates any newly arrived bytes without
     disturbing the scene. Serialising the work keeps two close nudges from
     reading and applying out of order. */
  useEffectB(() => {
    if (!engine || !boot || boot.id !== openId || !openId) return undefined;
    let alive = true;
    let applying = Promise.resolve();
    const durablePull = (event) => {
      const detail = event && event.detail;
      const pulledKeys = detail && Array.isArray(detail.pulledKeys) ? detail.pulledKeys : [];
      const sceneChanged = pulledKeys.includes(`board:${openId}`);
      const filesChanged = pulledKeys.some((key) => typeof key === 'string' && key.startsWith('boardfile:'));
      if (!sceneChanged && !filesChanged) return;
      applying = applying.then(async () => {
        if (!alive) return;
        if (sceneChanged) {
          const scene = await window.mdBoardLoad(openId);
          if (!alive) return;
          applyRemoteScene(scene.elements || []);
        }
        if (typeof window.mdBoardFilesFor === 'function') {
          const files = await window.mdBoardFilesFor(openId);
          if (!alive) return;
          const api = apiRef.current;
          if (api && typeof api.addFiles === 'function') api.addFiles(Object.values(files || {}));
        }
      }).catch(() => {});
    };
    window.addEventListener('mdsynced', durablePull);
    return () => { alive = false; window.removeEventListener('mdsynced', durablePull); };
  }, [engine, boot && boot.id, openId, applyRemoteScene]);

  /* Tell whoever mounted us whether there is anything here. The panel over a
     portrait board asks for this and nothing else: "Open board" is an
     invitation to look under it, and an invitation is worth making only when
     what is underneath cannot be seen — see md-app.jsx.

     It waits for `boot`. Every canvas begins its life reporting `false`,
     because nothing has been read yet, and sending that would flash the
     invitation across a board that is full — so the first word this says is
     the stored truth, not the placeholder before it. */
  useEffectB(() => {
    if (!boot || !onContent) return;
    onContent(hasBoardContent);
  }, [boot, hasBoardContent, onContent]);

  /* A link arriving on the board, from a drop or from a paste. One function for
     both because they differ in exactly one thing — whether there is a point on
     the canvas to land on — and the rest of it (is this media, make the record,
     pin it) must not be able to disagree between the two routes. */
  /* ANY address, not only a playable one. A link that cannot be framed still
     becomes a record and a card — it simply draws as a bookmark rather than a
     player (see md-embed.jsx). Refusing those used to mean a dragged article
     landed on the board as nothing at all, with no way to tell whether the drop
     had missed or the link had been rejected. */
  const takeUrl = useCallbackB((url, at) => {
    const embed = onAddMedia && window.mdEmbedLine ? window.mdEmbedLine(url) : null;
    if (!embed) return false;
    const ref = onAddMedia(url);
    if (!ref) return false;
    // the embed goes with the ref: the record it names does not exist in `ctx`
    // yet, and will not until this render is over. See sizeFor.
    pin(ref, at, embed);
    return true;
  }, [onAddMedia, pin]);

  /* ── what the ⋯ on a card can do ──────────────────────────────────────────
     THE DISTINCTION THIS MENU EXISTS FOR is the last two items. Taking a card
     off a board and deleting the writing behind it are completely different
     acts, and the engine's own delete button could only ever do the first while
     looking like the second. A pin is a view of a record (see this file's
     header); "Remove from board" forgets the arrangement, "Delete record"
     destroys what somebody wrote. Naming both, next to each other, is what
     makes either one safe to press. */
  const cardEl = useCallbackB(() => {
    const api = apiRef.current;
    if (!api || !card) return null;
    return api.getSceneElements().find((e) => e.id === card.id) || null;
  }, [card]);

  const cardTarget = card && window.mdBoardLinkNavigation?.(card.link);
  const cardFound = card && !cardTarget && window.mdRecordResolve
    ? window.mdRecordResolve(window.mdBoardLinkRef(card.link), ctx) : null;
  const cardEmbed = mdBoardEmbed(cardFound);

  const cardManifest = cardFound && (cardFound.craft || cardFound.manifest) ? (cardFound.craft || cardFound.manifest) : null;
  const [grantTick, setGrantTick] = useStateB(0);
  const cardGrant = cardManifest && window.mdGrantRead
    ? window.mdGrantRead(cardManifest.instance) : null;
  const manifestCaps = cardManifest && Array.isArray(cardManifest.capabilities)
    ? cardManifest.capabilities : null;
  const manifestMayRead = !manifestCaps || manifestCaps.some((cap) =>
    cap === 'records.read' || cap === 'records.write' || cap === 'projects.read' ||
    cap === 'prayers.read' || cap === 'navigation');
  const manifestMayWrite = !manifestCaps || manifestCaps.indexOf('records.write') >= 0;

  /* WHAT THE FILE SAYS IT REACHES, resolved to the library and field names a
     person would recognise. Read from the loaded bytes — mdManifestDeclsOf —
     and never from cardManifest.declarations, which is a stored claim that
     outlives the bytes it was written for. This menu is the only place consent
     is given, so it is the one place that must be describing the code that will
     actually run. Null means the frame has not finished loading; the Allow rows
     already refuse in that state. */
  const manifestDecls = cardManifest && window.mdCraftDeclsOf
    ? window.mdCraftDeclsOf(cardManifest.instance) : null;
  const manifestReach = manifestDecls && window.mdCraftReach
    ? window.mdCraftReach(manifestDecls) : null;

  /* And what has changed since the last yes. A new version keeps its grant as
     long as the digest matches; when it does not, the person is asked again —
     and being asked again in exactly the same words as the first time tells
     them nothing about what is different. This is the difference. */
  const reachDiff = cardGrant && cardGrant.reach && manifestDecls && window.mdCraftReachDiff
    ? window.mdCraftReachDiff(cardGrant.reach, manifestDecls) : null;

  /* A manifestation asked for something it has not been allowed. THE ASKING
     HAPPENS OUT HERE, in this app's own chrome, and never inside the frame —
     a permission prompt drawn by the thing requesting the permission is not a
     prompt, it is a suggestion from an interested party.

     It is a notice rather than a modal on purpose. A dashboard that wants to
     read the day should not be able to interrupt whatever somebody was doing
     the moment it loads; it should say what it wants and wait to be believed. */
  const askForManifest = useCallbackB((entry, need) => {
    if (!entry) return;
    setNotice({ kind: 'warn', text: need === 'write'
      ? `“${entry.name}” wants to change your records. Allow it from the ⋯ menu on its card.`
      : `“${entry.name}” wants to read your records. Allow it from the ⋯ menu on its card.` });
  }, []);

  const setGrant = useCallbackB((level) => {
    if (!cardManifest || !window.mdGrantSet) return;
    const digest = window.mdCraftDigestOf ? window.mdCraftDigestOf(cardManifest.instance) : '';
    if (!digest) { setNotice({ kind: 'error', text: 'That Craft is not loaded yet.' }); return; }
    /* The set parsed from the digested bytes, and never cardManifest.declarations
       — the grant records WHAT WAS ON SCREEN when somebody said yes, so a later
       version that reaches further can be described rather than re-asked in the
       same faceless words. Reading the entry here would record the entry
       writer's claim as the person's consent. */
    const reach = window.mdCraftDeclsOf ? window.mdCraftDeclsOf(cardManifest.instance) : null;
    window.mdGrantSet(cardManifest.instance, level, digest, reach);
    window.dispatchEvent(new CustomEvent('md:craft-permissions', {
      detail: { instance: cardManifest.instance, level },
    }));
    setGrantTick((n) => n + 1);
    setCardMenu(false);
    setNotice({ kind: 'ok', text: level === 'write'
      ? `“${cardManifest.name}” may now read and change your records.`
      : `“${cardManifest.name}” may now read your records.` });
  }, [cardManifest]);

  const clearGrant = useCallbackB(() => {
    if (!cardManifest || !window.mdGrantRevoke) return;
    window.mdGrantRevoke(cardManifest.instance);
    window.dispatchEvent(new CustomEvent('md:craft-permissions', {
      detail: { instance: cardManifest.instance, level: 'none' },
    }));
    setGrantTick((n) => n + 1);
    setCardMenu(false);
    setNotice({ kind: 'ok', text: `“${cardManifest.name}” may no longer reach your records.` });
  }, [cardManifest]);

  /* ── full screen ──────────────────────────────────────────────────────────
     ONE LIVE INSTANCE AT A TIME. An iframe cannot be moved in the DOM without
     reloading, and a React portal IS a DOM move — so there is no arrangement in
     which the same frame is both on the card and on the stage. Rather than have
     two copies of a manifestation running, both able to write, the card shows a
     quiet placeholder while the stage is open. Deterministic, and it costs one
     reload each way, which the "keep nothing durable in the DOM" rule already
     makes harmless.

     Not the Fullscreen API. The engine clears its active embeddable whenever
     document.fullscreenElement goes away, so using it would mean fighting the
     board over what is focused — and every other "open this bigger" surface in
     this app is already an overlay of the app's own. */
  const [stage, setStage] = useStateB(null);
  const openStage = useCallbackB(() => {
    if (!cardManifest) return;
    setStage(cardManifest);
    setCardMenu(false);
  }, [cardManifest]);
  const closeStage = useCallbackB(() => setStage(null), []);
  useEffectB(() => {
    const closeForNativeSurface = () => setStage(null);
    window.addEventListener('md:craft-native-open', closeForNativeSurface);
    return () => window.removeEventListener('md:craft-native-open', closeForNativeSurface);
  }, []);
  useEscapeB(closeStage, !!stage);

  /* Focus moves INTO the stage when it opens, and this is not only the
     accessibility rule about aria-modal dialogs — it is what makes Escape work
     at all. A keydown inside a cross-origin frame does not propagate to this
     document, so with focus left wherever it was, or taken by the frame, the
     Escape handler above simply never hears anything. Measured: the stage would
     not close.

     It cannot be fixed completely from here, and that is worth saying plainly
     rather than pretending: once somebody clicks INTO a manifestation, Escape
     belongs to that document until they click back out. The close button is
     always visible for exactly that reason. */
  const stageCloseRef = useRefB(null);
  useEffectB(() => {
    if (stage && stageCloseRef.current) stageCloseRef.current.focus();
  }, [stage]);

  const closeMenu = useCallbackB(() => setCardMenu(false), []);
  useEscapeB(closeMenu, cardMenu);

  const resetSize = useCallbackB(() => {
    const api = apiRef.current;
    const el = cardEl();
    if (!api || !el) return;
    const target = window.mdBoardLinkNavigation?.(el.link);
    const ref = target ? null : window.mdBoardLinkRef(el.link);
    const size = target
      ? { width: window.MD_BOARD_NAVIGATION_W, height: window.MD_BOARD_NAVIGATION_H }
      : sizeFor(ref);
    /* Grown from the same corner it was placed by, so a card that was nudged
       into a column does not jump out of it when it is put back to size. */
    const all = api.getSceneElementsIncludingDeleted
      ? api.getSceneElementsIncludingDeleted() : api.getSceneElements();
    api.updateScene({ elements: all.map((e) =>
      (e.id === el.id ? engine.newElementWith(e, { width: size.width, height: size.height }) : e)) });
    setCardMenu(false);
  }, [cardEl, sizeFor, engine]);

  const removeFromBoard = useCallbackB(() => {
    const api = apiRef.current;
    const el = cardEl();
    if (!api || !el || !engine.newElementWith) return;
    const all = api.getSceneElementsIncludingDeleted
      ? api.getSceneElementsIncludingDeleted() : api.getSceneElements();
    api.updateScene({ elements: all.map((e) => e.id === el.id && !e.isDeleted
      ? engine.newElementWith(e, { isDeleted: true }) : e) });
    setCardMenu(false);
    setCard(null);
  }, [cardEl, engine]);

  const copyMediaLink = useCallbackB(() => {
    if (!cardEmbed || !cardEmbed.href || !navigator.clipboard) return;
    /* The media's OWN address, never the `mandana.app/r/…` ref in the element.
       That ref is how this app names a record to itself; pasting it into a
       message would send somebody a link only this app can read. */
    navigator.clipboard.writeText(cardEmbed.href).then(
      () => setNotice({ kind: 'ok', text: 'Link copied.' }), () => {});
    setCardMenu(false);
  }, [cardEmbed]);

  /* A dropped document. Storing the bytes is asynchronous, so unlike a link
     this cannot pin synchronously — and a refusal has to be SAYABLE rather than
     silent, because a file that simply does not appear is indistinguishable
     from a broken app. Each error names the thing the person can act on. */
  /* A dropped manifestation. The bytes are already stored by the time this runs;
     what is left is to read what the document says it is, mint the identity the
     rows and the permission will hang from, and put a row in the library.

     The identity is minted HERE and not derived from the bytes, so dropping a
     new version onto the same portal later can keep the same instance and
     therefore the same data. Two files with identical bytes are one file in the
     store and two manifestations in the library, which is the right way round:
     the same dashboard used for two different things is two different things. */
  const takeManifest = useCallbackB(async (fileRef, file) => {
    if (!window.mdCraftId || !window.mdCraftPut) return null;
    let raw = '';
    try { raw = await file.text(); } catch (e) { raw = ''; }
    const source = window.mdCraftSource(raw);
    if (!source) {
      setNotice({ kind: 'error', text: file.size > (window.MD_CRAFT_MAX_BYTES || 0)
        ? 'That Craft is too large to open here.'
        : 'That file could not be read as a Craft.' });
      return null;
    }
    const instance = window.mdCraftId();
    const name = source.name || (window.mdFileNameOf && window.mdFileNameOf(file.name))
      || 'Craft';
    if (!window.mdCraftPut({ instance, file: fileRef.id, name, created: Date.now(),
      revision: 1, capabilities: source.capabilities || [], declarations: source.declarations })) {
      setNotice({ kind: 'error', text: 'That Craft could not be saved on this device.' });
      return null;
    }
    /* Said once, on the way in, because the frame's own badge is easy to miss and
       a dashboard whose chart library was refused looks broken rather than
       constrained. */
    if (source.dropped.length) {
      setNotice({ kind: 'warn', text: source.dropped.length === 1
        ? 'One external file was left out — a Craft cannot load anything from the network.'
        : `${source.dropped.length} external files were left out — a Craft cannot load anything from the network.` });
    } else if (window.mdCraftValidate) {
      const finding = window.mdCraftValidate(source).find((row) => row.level === 'warning');
      if (finding) setNotice({ kind: 'warn', text: finding.message });
    }
    return window.mdCraftRef(instance);
  }, []);

  const takeFile = useCallbackB(async (file, at) => {
    if (!onAddFile || !window.mdFilePut) return;
    /* THE OWNER IS MINTED BEFORE THE BYTES ARE OFFERED, and the order is the
       whole of this. A blob's hold belongs to the record that owns it, so
       that deleting one of six records naming the same PDF takes away one
       hold and not the document — and a record that does not exist yet cannot
       be named. The id used to be minted by the day, AFTER the upload, so
       every file dropped on a desk was held under `legacy` and nothing short
       of deleting the account could ever let it go.

       Minting one here that the day then ignored would be the same bug with
       an extra step, so it is handed over below and the day uses it. */
    const uid = window.mdRecordUid ? window.mdRecordUid() : '';
    const put = await window.mdFilePut(file, uid ? { ref: `record:${uid}` } : undefined);
    if (!put.ok) {
      setNotice({ kind: 'error', text: put.error === 'too_large'
        ? `That file is too large to keep here (the limit is about ${Math.round(window.MD_FILE_MAX_BYTES / (1024 * 1024))} MB).`
        : put.error === 'unsupported_type'
        ? 'A desk takes PDFs and Crafts (.html) for now.'
          /* The local write is the first thing that happens and nothing is
             offered to the store until it succeeds, so this really is the
             whole answer: the file is nowhere. Saying only "not on this
             device" would now read as "it synced anyway", which it did not. */
          : 'That file could not be saved on this device, so it has not reached your other devices either.' });
      return;
    }

    /* A manifestation forks here and never touches the record path. It is not a
       document a record refers to — it is its own thing in the library, and the
       portal names it by ref. Which is also what keeps HTML out of the embed
       grammar entirely: no `mdfile:` line is ever written for one. */
    if (window.mdFileIsCraft && window.mdFileIsCraft(put.ref.type)) {
      const ref = await takeManifest(put.ref, file);
      if (ref) pin(ref, at);
      return;
    }

    const ref = onAddFile(put.ref, uid);
    if (ref) pin(ref, at, { kind: 'file' });
  }, [onAddFile, pin, takeManifest]);

  /* ── a manifestation that arrived as text ─────────────────────────────────
     The same document, minus the file. Somebody asks for a dashboard, gets the
     HTML back in a conversation, and pastes it — and requiring them to save it
     to disk first, find it in Finder and drag it in is three steps standing
     between a document and the board it was written for.

     It becomes a File before anything else touches it, so there is exactly one
     intake path: mdFilePut hashes and stores it, mdFileIsManifest routes it, and
     takeManifest names it and mints its identity. Nothing here knows what a
     manifestation IS — it only knows how to turn text into the thing the drop
     path already understood.

     The filename is a fallback and says what it is. A document with a <title>
     is named by it (takeManifest prefers source.name); one without becomes
     "Pasted manifestation", which is true and renameable, rather than a guess.

     Nothing about the paste route relaxes the sandbox: the same opaque origin,
     the same injected policy, the same grant of `none` until somebody says
     otherwise. What it does change is how EASY this is to do at somebody else's
     suggestion — "copy this and paste it into your app" is a shorter sentence
     than one about downloading a file. That is exactly what the grant is for. */
  const takePastedManifest = useCallbackB(async (raw) => {
    const type = window.MD_CRAFT_MIME;
    if (!type || typeof File !== 'function') return;
    await takeFile(new File([raw], 'Pasted Craft.html', { type }), null);
  }, [takeFile]);

  /* Where on the canvas a pointer event happened, in scene coordinates. The
     inverse of the mapping `pin` uses: sceneX = viewportX / zoom - scrollX. */
  const scenePoint = useCallbackB((clientX, clientY) => {
    const api = apiRef.current;
    const wrap = wrapRef.current;
    if (!api || !wrap) return null;
    const st = api.getAppState();
    const z = (st.zoom && st.zoom.value) || 1;
    const rect = wrap.getBoundingClientRect();
    return { x: (clientX - rect.left) / z - st.scrollX, y: (clientY - rect.top) / z - st.scrollY };
  }, []);

  /* ── dropping a link on the board ─────────────────────────────────────────
     Excalidraw 0.18 has an `onPaste` prop and NO `onDrop`, so a drop is a real
     DOM listener on the wrapper. Two things about it are deliberate:

     • it listens in the CAPTURE phase, because the engine's own drop handler is
       on the canvas inside this element and would otherwise get there first;
     • it claims a link and NOTHING else. An image file, a .excalidraw scene,
       anything with `files` on it — untouched, straight through to the engine,
       so today's paste-a-screenshot path behaves exactly as it did.

     `dragover` has to be answered too, and separately: a drop event never fires
     at all unless something has called preventDefault on the dragover before
     it. The data is unreadable during a drag (the browser withholds it until
     the drop, deliberately), so the most that can be checked there is that a
     URL is among the offered types. */
  useEffectB(() => {
    const wrap = wrapRef.current;
    if (!wrap || !engine) return;

    const has = (dt, t) => !!dt && Array.prototype.indexOf.call(dt.types || [], t) >= 0;

    const onDragOver = (e) => {
      /* During a drag the browser withholds the data deliberately, so the most
         that can be asked is which KINDS are on offer. A file's type is not
         readable here either — so a file drag is accepted at this stage and
         handed back to the engine at drop time if it turns out not to be ours. */
      const navType = window.MD_NAVIGATION_DRAG_TYPE;
      if (!(navType && has(e.dataTransfer, navType))
        && !has(e.dataTransfer, 'text/uri-list') && !has(e.dataTransfer, 'Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    };

    const onDrop = (e) => {
      const dt = e.dataTransfer;
      if (!dt) return;
      /* Account-owned data wins before its ordinary share URL. That URL stays
         on the drag for other apps, but treating it as external here would mint
         a duplicate bookmark record for a destination ManDana already owns. */
      const navType = window.MD_NAVIGATION_DRAG_TYPE;
      const navTarget = navType ? dt.getData(navType) : '';
      if (navTarget) {
        const at = scenePoint(e.clientX, e.clientY);
        if (!pinNavigation(navTarget, at)) return;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      /* A FILE. Only the kinds md-file.ts will store are claimed — an image
         file keeps falling through to the engine, which already stores it,
         resizes it and SYNCS it under the 192 KB cap. Routing images here for
         consistency's sake would take away sync they already have. */
      const file = dt.files && dt.files.length === 1 ? dt.files[0] : null;
      if (file && window.mdFileKind && window.mdFileKind(file.type)) {
        e.preventDefault();
        e.stopPropagation();
        takeFile(file, scenePoint(e.clientX, e.clientY));
        return;
      }
      if (dt.files && dt.files.length) return;         // the engine's business
      /* uri-list is what a drag from a browser's address bar or a link carries;
         it can hold several lines and comments, so the first real line wins. */
      const raw = dt.getData('text/uri-list') || dt.getData('text/plain') || '';
      const url = raw.split('\n').map((s) => s.trim()).find((s) => s && s[0] !== '#') || '';
      if (!url) return;
      const at = scenePoint(e.clientX, e.clientY);
      if (!takeUrl(url, at)) return;                    // not media — leave it alone
      e.preventDefault();
      e.stopPropagation();
    };

    wrap.addEventListener('dragover', onDragOver, true);
    wrap.addEventListener('drop', onDrop, true);
    return () => {
      wrap.removeEventListener('dragover', onDragOver, true);
      wrap.removeEventListener('drop', onDrop, true);
    };
  }, [engine, takeUrl, takeFile, pinNavigation, scenePoint]);

  /* ── pasting a manifestation onto the board ───────────────────────────────
     The engine has an onPaste prop and a link goes through it, so the obvious
     place for this is there. It is the wrong place, twice over, and both
     reasons were measured rather than reasoned about:

     • THE ENGINE IGNORES A PASTE UNLESS FOCUS IS ALREADY INSIDE ITS CONTAINER.
       Read out of its source: `n = document.activeElement; i = container
       .contains(n); if (event && !i) return`. Open a board, switch to another
       app to copy the HTML, come back, press paste — activeElement is the body
       and nothing happens, with no way to tell that from a broken feature. That
       rule is right for the engine (it stops a canvas from eating a paste meant
       for a text field) and wrong for this, which is why the guard below is
       narrower rather than absent.

     • A FILE ON THE CLIPBOARD DOES NOT SURVIVE THE TRIP. The engine awaits its
       own clipboard parse before calling onPaste, and a DataTransfer becomes
       protected once its event has finished dispatching — `files` is empty by
       then. Reading it has to happen while the event is live, which is here.

     So: document, capture phase, before the engine's own document listener. It
     claims only what a board provably takes and lets everything else past
     untouched — including, deliberately, a paste into anything somebody could
     be typing in. A search box inside a board view is still a search box. */
  useEffectB(() => {
    if (!engine) return undefined;

    const writable = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!el.isContentEditable;
    };

    const onPasteCapture = (e) => {
      if (!e.clipboardData || writable(document.activeElement)) return;

      /* Read synchronously, before anything is awaited — see above. The same
         predicate the drop handler uses, so a board cannot take a file one way
         and refuse it the other. An image still falls through to the engine,
         which stores, resizes and syncs it. */
      const files = e.clipboardData.files;
      const file = files && files.length === 1 ? files[0] : null;
      if (file && window.mdFileKind && window.mdFileKind(file.type)) {
        e.preventDefault();
        e.stopPropagation();
        takeFile(file, null);
        return;
      }
      if (files && files.length) return;                 // the engine's business

      const text = (e.clipboardData.getData('text/plain') || '').trim();
      if (!text || !window.mdCraftLooksLike || !window.mdCraftLooksLike(text)) return;
      e.preventDefault();
      e.stopPropagation();
      takePastedManifest(text);
    };

    document.addEventListener('paste', onPasteCapture, true);
    return () => document.removeEventListener('paste', onPasteCapture, true);
  }, [engine, takeFile, takePastedManifest]);

  /* White paper in light, true black in dark. Not a preference and not stored
     per board — see APP_STATE_KEEP in app/md-board.ts.

     The engine is told the canvas has NO background, and the colour is painted
     by the div underneath instead. That indirection is not decoration: dark
     mode in Excalidraw is `filter: invert(0.93) hue-rotate(180deg)` over the
     whole canvas, so a background of #000 comes out WHITE and the darkest
     reachable value is about #121212 — the number is a fudge factor, and no
     colour can beat it, because inverting 93% of white still leaves 7%.
     Transparent pixels keep their alpha through a filter, so painting behind
     the canvas steps around the inversion entirely and the paper is exactly the
     colour asked for, in both themes. */
  const paper = 'transparent';
  const behind = light ? '#FAFBFC' : '#000000';

  /* ── the engine's chrome, in the app's colours ────────────────────────────
     Excalidraw's toolbar, zoom, undo/redo and style panel are all painted from
     a set of CSS custom properties it publishes on `.excalidraw` — island fill,
     surface steps, primary, radii, shadow. Restyling them is therefore a matter
     of answering those properties rather than fighting fifty selectors, and it
     leaves the engine's own layout, icons and states entirely alone.

     They are set from HERE and not from the stylesheet because half of them are
     the accent, and the accent is a value this app keeps in JavaScript. The
     names are ours (`--md-b-*`); index.html maps them onto Excalidraw's, which
     is also where the shapes live — radii, hairlines, the frosting. Two files,
     but the split is the same one the rest of this component already makes: the
     colours where the theme is, the geometry where the CSS is.

     Dark still departs from Glass, and for the reason it always did: the app
     drops backdrop-filter in dark because there is nothing behind a card but a
     flat backdrop and Chromium ghosts it while scrolling. Here there IS
     something behind — the drawing — so the chrome keeps a real pane and a
     genuine blur, and a stroke never reads through the toolbar.

     What it no longer departs from is the app's frost GROUND. This block used
     to name a dark fill of its own, `rgba(16,18,22,0.76)`: lighter than the
     frost, and a quarter see-through where the frost is opaque. Marwan saw the
     difference on the screen — the desk toolbar beside the Tabs surface, not
     quite the same grey — and asked for the two unified (2026-08-24). So the
     fill is `mdFrostGround` now, in both themes, and the board's chrome is the
     same material as every other panel in the app.

     The cost is named rather than hidden: at 0.98 the pane is near-opaque, so
     the drawing no longer shows faintly through the chrome the way it did. The
     blur still has work to do at the island's edges, and the see-through was
     the thing that made the toolbar read as its own material — which is exactly
     what was not wanted. */
  const chrome = {
    /* The engine paints selection into the CANVAS, not the DOM, so no
       stylesheet can reach it — but it reads its colour from here first:
       `getComputedStyle(container).getPropertyValue("--color-selection")`,
       falling back to its own #6965db. That fallback is the purple in every
       screenshot of this board, and it is the one colour on the screen that
       belongs to a different product. This hands it the app's accent instead,
       so selecting a card looks like selecting anything else in ManDana. */
    '--color-selection': accent,
    '--md-b-ink': inkB(0.82),
    '--md-b-dim': inkB(0.34),
    '--md-b-accent': accent,
    '--md-b-violet': light ? '#7C68E6' : '#C4B5FF',
    /* The selected tool gets an accent wash with full-strength ink on it —
       the same two moves the board switcher makes for the open board, so
       "this one is chosen" reads identically on both sides of the screen. */
    '--md-b-sel': rgbaB(accent, light ? 0.16 : 0.28),
    '--md-b-sel-ink': inkB(0.96),
    /* The island fill is the app's ONE frost ground, from the one author, in
       both themes — so it sits outside the light/dark fork below. Light already
       stated the dress's exact value; dark is the half that moves. */
    '--md-b-glass': mdFrostGround(light),
    ...(light ? {
      '--md-b-edge': 'rgba(255,255,255,0.84)',
      '--md-b-field': 'rgba(30,34,42,0.045)',
      '--md-b-hover': inkB(0.07),
      '--md-b-shadow': 'inset 0 1px rgba(255,255,255,0.72), 0 26px 80px rgba(28,34,46,0.19)',
    } : {
      '--md-b-edge': 'rgba(255,255,255,0.13)',
      '--md-b-field': 'rgba(255,255,255,0.07)',
      '--md-b-hover': inkB(0.10),
      '--md-b-shadow': 'inset 0 1px rgba(255,255,255,0.055), 0 30px 90px rgba(0,0,0,0.58)',
    }),
  };

  useEffectB(() => {
    let alive = true;
    mdLoadEngine().then(
      (mod) => { if (alive) setEngine(mod); },
      (err) => { if (alive) setFailed(err && err.message === 'unbuilt' ? 'unbuilt' : 'failed'); },
    );
    return () => { alive = false; };
  }, []);

  /* The ⋯ toggle is session-scoped and lives outside React because the menu is
     seated inside Excalidraw's imperative toolbar. This event is the bridge:
     switching it off — manually or after three idle minutes — tears the room
     down on this render, while durable sync keeps running as before. */
  useEffectB(() => {
    const read = () => setCollabEnabled(typeof window.mdCollabEnabled === 'function'
      && window.mdCollabEnabled(openId));
    window.addEventListener('mdcollabcontrol', read);
    read();
    return () => window.removeEventListener('mdcollabcontrol', read);
  }, [openId]);

  /* Local interaction is the idle clock. Remote frames deliberately do not
     count: another device drawing should not keep an unattended screen in the
     room forever. Updating the clock is O(1); md-collab owns the single timer. */
  useEffectB(() => {
    if (!wrapEl || !collabEnabled || typeof window.mdCollabTouch !== 'function') return undefined;
    const touch = () => window.mdCollabTouch(openId);
    for (const name of ['pointermove', 'pointerdown', 'keydown', 'wheel']) {
      wrapEl.addEventListener(name, touch, { passive: true });
    }
    return () => {
      for (const name of ['pointermove', 'pointerdown', 'keydown', 'wheel']) {
        wrapEl.removeEventListener(name, touch);
      }
    };
  }, [wrapEl, openId, collabEnabled]);

  /* The desk room exists only while this canvas can actually be acted on.
     Hidden tabs and covered desks leave immediately; returning joins with a
     fresh per-tab actor and asks an incumbent for a snapshot. */
  /* A newly added image travels shell-first (its element in the scene frame),
     then its bytes through the blob store, then the name that joins them. A
     reconciled remote image is not ours to re-announce. */
  const publishNewImages = (changes, files) => {
    const session = collabRef.current;
    if (!session || typeof session.publishAsset !== 'function'
      || typeof window.mdBoardDataBytes !== 'function') return;
    for (const el of changes) {
      if (!(el && el.type === 'image' && el.fileId)) continue;
      if (publishedAssetsRef.current.has(el.fileId)) continue;
      publishedAssetsRef.current.add(el.fileId);
      const file = files && files[el.fileId];
      const bytes = file && file.dataURL ? window.mdBoardDataBytes(file.dataURL) : null;
      if (bytes) {
        void session.publishAsset({ fileId: el.fileId, mimeType: file.mimeType }, bytes)
          .catch(() => { publishedAssetsRef.current.delete(el.fileId); });
      } else {
        publishedAssetsRef.current.delete(el.fileId);
      }
    }
  };

  useEffectB(() => {
    if (!collabEnabled || !active || muted || !engine || !boot || boot.id !== openId || !openId
      || typeof window.mdCollabJoin !== 'function') return undefined;
    let disposed = false;
    let joining = false;
    let held = null;

    const collaborators = (peers) => new Map(peers.map((peer) => [peer.actor, {
      socketId: peer.actor,
      username: peer.name,
      pointer: peer.pointer ? { ...peer.pointer, renderCursor: true } : undefined,
      button: peer.button || 'up',
      selectedElementIds: Object.fromEntries((peer.selected || []).map((id) => [id, true])),
      userState: 'active',
    }]));

    const leave = () => {
      const session = held;
      held = null;
      collabRef.current = null;
      setCollabPeers([]);
      setCollabState('closed');
      if (session) void session.leave();
    };

    const enter = async () => {
      if (disposed || joining || held || document.visibilityState === 'hidden') return;
      joining = true;
      setCollabState('connecting');
      const joined = await window.mdCollabJoin(openId, {
        snapshot: () => {
          const api = apiRef.current;
          if (!api) return [];
          return api.getSceneElementsIncludingDeleted
            ? api.getSceneElementsIncludingDeleted()
            : api.getSceneElements();
        },
        onScene: (remote) => {
          applyRemoteScene(remote);
        },
        onPeers: (peers) => {
          if (disposed) return;
          setCollabPeers(peers);
          const api = apiRef.current;
          if (api) api.updateScene({ collaborators: collaborators(peers),
            captureUpdate: engine.CaptureUpdateAction && engine.CaptureUpdateAction.NEVER });
        },
        /* The files the joining peer will need named. Only references travel —
           a picture's bytes move through the blob store, so what a snapshot
           owes a newcomer is the list of hashes its image elements point at,
           not the pictures themselves. */
        assets: async () => {
          try {
            return typeof window.mdBoardAssetManifests === 'function'
              ? await window.mdBoardAssetManifests(openId) : [];
          } catch { return []; }
        },
        /* A peer's picture arrives as a name; its bytes follow. null bytes mean
           not here yet — the shell already renders from onScene, so retry is
           the whole job. Received bytes are kept as this desk's own file. */
        onAsset: async (asset, bytes) => {
          if (disposed || !asset || !asset.hash) return;
          if (asset.fileId) publishedAssetsRef.current.add(asset.fileId);
          if (!(bytes instanceof Uint8Array) || !bytes.byteLength) return;
          const b64 = typeof window.mdBoardToBase64 === 'function' ? window.mdBoardToBase64(bytes) : '';
          if (!b64) return;
          const mimeType = asset.mimeType || 'image/png';
          const dataURL = 'data:' + mimeType + ';base64,' + b64;
          try {
            if (typeof window.mdBoardFilePut === 'function' && asset.fileId) {
              await window.mdBoardFilePut({ id: asset.fileId, dataURL, mimeType }, openId);
            }
            const api = apiRef.current;
            if (api && typeof api.addFiles === 'function') {
              api.addFiles([{ mimeType, id: asset.fileId, dataURL, created: Date.now() }]);
            }
          } catch { /* a shell stays a shell until the bytes land */ }
        },
        onState: (state) => { if (!disposed) setCollabState(state.state); },
      });
      joining = false;
      if (disposed || document.visibilityState === 'hidden') {
        if (joined && joined.ok) await joined.leave();
        return;
      }
      if (joined && joined.ok) {
        held = joined;
        collabRef.current = joined;
      } else setCollabState('closed');
    };

    const visibility = () => {
      if (document.visibilityState === 'hidden') leave();
      else void enter();
    };
    document.addEventListener('visibilitychange', visibility);
    void enter();
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', visibility);
      leave();
    };
  }, [collabEnabled, active, muted, engine, boot && boot.id, openId, applyRemoteScene]);

  /* ── PRESENCE FOLLOWS THE DESK ───────────────────────────────────────────
     Which desk this device is on is a fact about the SCREEN, and it used to be
     a side effect of the effect above: the room told md-live.ts, so the answer
     existed only while the ⋯ live-sync toggle was on — session-scoped, off by
     default, and off again after three idle minutes.

     What that made false is a sentence, not a feature. Settings reads this per
     device and says "On this desk", "On another desk" or "Online elsewhere",
     and the last of those is what a person saw about an iPad that had the
     drawing open right in front of them. That is the one screen anybody
     consults when they are working out which device holds their work, which
     makes a wrong answer there worse than no answer.

     So it is said here, from the canvas being up, and the room says nothing
     about it. Not gated on the engine or the boot either: a desk is on screen
     from the moment its shell is, and somebody reading Settings on their phone
     is asking where their drawing is — not whether it has finished loading on
     the other machine. The frame carries an id and never content, and
     md-live.ts refuses anything that is not a desk id. */
  useEffectB(() => {
    if (typeof window.mdLiveSetActiveDesk !== 'function') return undefined;
    const here = !!openId && active && !muted;
    window.mdLiveSetActiveDesk(here ? openId : null);
    return () => { window.mdLiveSetActiveDesk(null); };
  }, [openId, active, muted]);

  /* The paper follows Settings while the board is open, not just at mount —
     `initialData` is read once, so without this switching to Light would leave
     a black rectangle sitting in a white app until the next reload. */
  useEffectB(() => {
    if (apiRef.current) apiRef.current.updateScene({ appState: { viewBackgroundColor: paper } });
  }, [paper, engine, openId]);

  /* ── where the selected card is, on screen ────────────────────────────────
     The engine's own mapping, inverted: a scene point is at
     (scene + scroll) × zoom inside the container. This runs on EVERY change —
     including the ones that fire continuously while dragging — so it does the
     least work it can and, crucially, returns the SAME OBJECT when nothing it
     cares about moved. Setting fresh state on every pointer move would re-render
     the canvas's siblings sixty times a second for no visible difference.

     Only a single selection counts. Two cards selected together is a moment for
     moving them, not for acting on one of them, and a ⋯ hovering over an
     ambiguous target is worse than none. */
  /* ── the zoom row's two jobs ──────────────────────────────────────────────
     The arithmetic is md-board.ts's and it is the engine's own (see
     mdBoardZoomTo): hold the scene point under the middle of the screen still.
     Here it is only plumbed — read the engine's state, hand it back the scroll
     and zoom the pure function returned.

     `captureUpdate` is left off deliberately, and that was read rather than
     assumed: `updateScene` only asks its store to capture when a captureUpdate
     is passed AND is not EVENTUALLY, so omitting it puts nothing on the undo
     stack. The engine's own zoom actions say EVENTUALLY for the same reason —
     a zoom is not an edit, and Undo must spend its presses on strokes rather
     than on restoring viewports.

     AND A PRESS COMPOSES ONTO THE LAST ONE, not onto what the engine has
     caught up to. `updateScene` does not write appState synchronously, so two
     taps in one tick both read 100% and the board moved a single step. What
     the pure function returns IS a state it can be handed back, so the last
     request is kept and merged over the engine's until the engine reports —
     and the report clears it, so a pan or a pinch is never composed onto a
     stale number. */
  const zoomBy = useCallbackB((dir) => {
    const api = apiRef.current;
    if (!api) return;
    const base = { ...api.getAppState(), ...(zoomPendingRef.current || {}) };
    const next = window.mdBoardZoomStep(base, dir);
    zoomPendingRef.current = next;
    api.updateScene({ appState: next });
  }, []);
  const zoomReset = useCallbackB(() => {
    const api = apiRef.current;
    if (!api) return;
    const next = window.mdBoardZoomTo({ ...api.getAppState(), ...(zoomPendingRef.current || {}) }, 1);
    zoomPendingRef.current = next;
    api.updateScene({ appState: next });
  }, []);
  /* A pinch changes the zoom sixty times a second and re-renders nothing here,
     so the readout is state — but only when the whole number it prints has
     actually changed, which is the same bargain `trackCard` strikes below. */
  const trackZoom = useCallbackB((appState) => {
    zoomPendingRef.current = null;
    const pct = Math.round(((appState.zoom && appState.zoom.value) || 1) * 100);
    setZoomPct((before) => (before === pct ? before : pct));
  }, []);

  const trackCard = useCallbackB((elements, appState) => {
    const ids = appState.selectedElementIds || {};
    let only = null;
    for (const el of elements) {
      if (!ids[el.id] || el.isDeleted) continue;
      if (only) { only = null; break; }               // more than one: no menu
      if (el.type !== 'embeddable') { only = null; break; }
      only = el;
    }
    if (!only) { setCard((c) => (c === null ? c : null)); return; }
    const z = (appState.zoom && appState.zoom.value) || 1;
    const next = {
      id: only.id, link: only.link,
      x: (only.x + appState.scrollX) * z,
      y: (only.y + appState.scrollY) * z,
      w: only.width * z, h: only.height * z,
    };
    setCard((c) => (c && c.id === next.id && Math.abs(c.x - next.x) < 0.5
      && Math.abs(c.y - next.y) < 0.5 && Math.abs(c.w - next.w) < 0.5
      && Math.abs(c.h - next.h) < 0.5 ? c : next));
  }, []);

  /* ── the engine's link badge, covered ─────────────────────────────────────
     A pinned card carries a `link` because that is how it names its record, and
     the engine draws a blue open-link badge above the top-right corner of every
     element that has one. There is no way to turn it off: the renderer's only
     test is `element.link && !selected` — no flag, no appState, no prop — and it
     paints straight into the canvas where no stylesheet can reach it. Verified
     in the bundle rather than assumed, and the alternative was checked too:
     taking the link off makes the engine draw "Empty Web-Embed" and never call
     renderEmbeddable at all, so the link is load-bearing.

     So the badge is covered, in the board's own paper colour, by a patch that
     rides each card the way the ⋯ does. The geometry is the engine's own, read
     off the same function it draws with: a 14px square, one pixel right of the
     element and fifteen above it, at every zoom (the size divides by zoom in
     scene units and multiplies back on screen, so it is constant in pixels).

     ONLY UNSELECTED CARDS get one, which is exactly when the badge is drawn —
     and it matters twice over, because a selected card's top-right resize
     handle lives in that same square and a patch over it would eat the corner
     you resize from.

     The colour is `behind`, not `paper`: the engine is told the canvas has no
     background and the board's colour is painted by the div underneath it (see
     the note there), so `behind` IS the paper and a patch in it is invisible.

     It is written straight to the DOM rather than held in state. This runs on
     every pointer move of every pan; putting an array of rectangles through
     React sixty times a second would re-render the canvas's siblings for a
     handful of 14px squares. */
  const MASK = 14, MASK_PAD = 2;
  const maskLinks = useCallbackB((elements, appState) => {
    const layer = maskRef.current;
    if (!layer) return;
    const z = (appState.zoom && appState.zoom.value) || 1;
    const ids = appState.selectedElementIds || {};
    let i = 0;
    for (const el of elements) {
      if (el.isDeleted || !el.link || ids[el.id] || el.angle) continue;
      let box = layer.children[i];
      if (!box) {
        box = document.createElement('span');
        box.setAttribute('aria-hidden', 'true');
        box.style.position = 'absolute';
        box.style.pointerEvents = 'none';
        box.style.width = `${MASK + MASK_PAD * 2}px`;
        box.style.height = `${MASK + MASK_PAD * 2}px`;
        layer.appendChild(box);
      }
      box.style.background = behind;
      box.style.transform = `translate(${Math.round((el.x + el.width + appState.scrollX) * z + 1 - MASK_PAD)}px,`
        + `${Math.round((el.y + appState.scrollY) * z - 15 - MASK_PAD)}px)`;
      i++;
    }
    while (layer.children.length > i) layer.removeChild(layer.lastChild);
  }, [behind]);

  /* Says it once. A sentence the person has closed is not said again until the
     save has something else to report — which is also what clears the memory of
     it, below, on the save that has nothing to say at all. */
  const raise = useCallbackB((n) => {
    if (dismissed.current === n.text) return;
    dismissed.current = null;
    setNotice(n);
  }, []);

  /* THE CANVAS UNDER THIS IS BLANK AND THE DESK IS NOT, and saying so is the
     whole of the promise. mdBoardSave already refuses to write the blankness
     down, so nothing has been lost — but a blank desk that says nothing reads
     as a deleted one, and that is the fear worth answering out loud.

     Here rather than in the read above, because `raise` is declared further
     down this component and an effect cannot name it before it exists. */
  useEffectB(() => {
    if (!boot || boot.id !== openId || !boot.unreadable) return;
    raise({ kind: 'warn', text: boot.unreadable === 'spill'
      ? 'This desk\u2019s drawing is still arriving from your other device. Nothing has been lost — it opens as soon as the drawing gets here.'
      : 'This desk can\u2019t be read right now — nothing on it has been lost.' });
  }, [boot, openId, raise]);

  /* AND ASK AGAIN WITHOUT BEING ASKED. A fault is almost always a database
     connection the operating system closed while the tab was away — which
     makes coming back to the tab the signal, because it is the same event.
     One immediate attempt (the store may already be well), then one on every
     return, bounded so a store that flaps cannot turn this into a loop; a
     return refills the budget, because hours may have passed.

     A spill is not retried here: those bytes come from another device, and the
     pull that brings them already repaints this desk. */
  const autoRetries = useRefB({ id: null, n: 0 });
  useEffectB(() => {
    if (!boot || boot.id !== openId || boot.unreadable !== 'fault') return undefined;
    if (autoRetries.current.id !== openId) autoRetries.current = { id: openId, n: 0 };
    let alive = true;
    const again = (event) => {
      if (event) autoRetries.current = { id: openId, n: 0 };
      if (!alive || autoRetries.current.n >= MD_DESK_AUTO_RETRIES) return;
      if (document.visibilityState === 'hidden') return;
      autoRetries.current.n += 1;
      Promise.resolve(window.mdBoardRetry ? window.mdBoardRetry(openId) : 'fault').then((why) => {
        if (!alive || why !== 'ok') return;
        dismissed.current = null;
        setNotice(null);
        setReboot((n) => n + 1);
      }).catch(() => {});
    };
    again(null);
    document.addEventListener('visibilitychange', again);
    window.addEventListener('pageshow', again);
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', again);
      window.removeEventListener('pageshow', again);
    };
  }, [boot, openId]);

  /* Saving is debounced because onChange fires on every pointer move while a
     stroke is being drawn — writing the whole scene to storage at that rate
     would make drawing itself stutter. 600ms is long enough to be free during a
     stroke and short enough that closing the tab straight after one keeps it. */
  /* Bytes as a person would say them. The cap used to be printed as KB
     unconditionally, which was fine at 192 KB and reads as "8192 KB" now. A
     limit the user cannot picture is a limit they cannot act on. */
  const mdSizeWords = (bytes) => (bytes >= 1024 * 1024
    ? `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`
    : `${Math.round(bytes / 1024)} KB`);

  const save = useCallbackB((elements, appState, files) => {
    /* AND ONLY WHEN THERE IS SOMETHING TO SAVE. onChange is the engine saying
       it re-rendered, not that the drawing moved — it fires on its own while
       the desk sits open and untouched, and every one of those used to become
       a hash, an IndexedDB write and a sync nudge about a second apart. The
       stamp is what a save would actually write (md-board.ts); unchanged means
       there is nothing to write, so the timer is not even armed. */
    const stamp = window.mdBoardSceneStamp({ elements, appState }, files);
    if (stamp === savedStamp.current) return;
    savedStamp.current = stamp;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const res = await window.mdBoardSave(openId, { elements, appState });
      readTransfers();
      /* Name the cause, not just the verdict. "Storage is full or blocked" was
         technically true and practically useless: full means delete something,
         blocked means leave private browsing or unblock cookies, and the two
         have nothing in common beyond the thrown error. The store knows which
         one happened — the banner says it. */
      /* A DESK WHOSE DRAWING IS NOT HERE YET. After 1.8 a large scene lives in
         a blob and the document only names it, so a device that has pulled the
         name but not the bytes has a desk it cannot draw — and mdBoardSave
         refuses rather than writing an empty canvas over a name that still
         means something. Nothing is wrong with this browser and nothing has
         been lost, so neither of the two sentences below is true of it. */
      if (res.stranded) raise({ kind: 'warn', text: res.fault
        ? 'This desk can\u2019t be read right now — nothing was saved over it, and nothing on it has been lost.'
        : 'This desk\u2019s drawing is still arriving from your other device. Nothing was saved over it — it opens as soon as the drawing gets here.' });
      /* The guard forgets, because a scene that failed to land is not saved
         and must not be remembered as saved. That is deliberately the one
         state where an untouched desk retries by itself: a device that was out
         of space when the stroke was drawn should write it the moment there is
         room, and losing the drawing is the worse trade.

         A STRANDED DESK KEEPS ITS STAMP, both kinds of it. Nothing failed
         there — the bytes are still arriving, or the desk could not be read —
         and in both cases the thing that ends the wait also moves the stamp
         without being asked: an arrival repaints the canvas, and a re-read
         re-seeds the stamp in the boot effect above. Forgetting it here would
         buy nothing and put the once-a-second write back in the one state
         where a device is already struggling. */
      else if (!res.ok) { savedStamp.current = null; raise({ kind: 'error', text: res.quota
        ? 'This desk could not be saved — this device is out of storage. Free some space, then make any small edit to retry.'
        : 'This desk could not be saved — this browser is blocking storage. If this is a private window, that is why.' }); }
      /* Says what happened, then what to do about it. The old wording stopped at
         the verdict, which left the only apparent remedy as "draw less" — and
         gave no hint that the drawing was safe. */
      else if (res.oversize) raise({ kind: 'warn', text: 'This desk is saved here in full, but it is too big to sync — it will not reach your other devices. Removing some strokes, or splitting it across two desks, brings it back.' });
      else {
        dismissed.current = null;
        setNotice((n) => (n && n.kind !== 'image' ? null : n));
        /* Persistence is recovery, not rendering. A remote element has already
           painted by the time this quiet save runs; the durable path follows
           once, after the burst, so an asleep device can still recover it. */
        if (typeof window.mdSyncSoon === 'function') window.mdSyncSoon(750);
      }

      /* Images are stored one document each, beside the board rather than
         inside it: storage unit = sync unit, and a screenshot pasted into the
         scene would otherwise carry the whole drawing over the cap in one go.
         One that will not fit is REFUSED and its element removed, because an
         image silently absent after the next reload is the worse outcome. */
      const rejected = [];
      /* Kept here in full, but over the server's document cap, so it will not
         travel. Silence was the wrong answer: the picture is on the board and
         looks saved BECAUSE it is saved, and the user would meet the gap as a
         mysteriously missing image on their iPad days later. The drawing beside
         it already says this for itself (`res.oversize` above); this is the same
         sentence for the picture. */
      let stranded = false;
      for (const [id, file] of Object.entries(files || {})) {
        if (!file || !file.dataURL) continue;
        /* HAVE I GOT THIS, not give me this. mdBoardFileGet resolves the
           document into the data URL Excalidraw wants, which after 1.7 means
           fetching the bytes and base64-encoding the whole picture — paid on
           every save, to answer a yes/no question. */
        if (await window.mdBoardFileHas(id)) continue;
        /* And the desk is named explicitly rather than read back out of
           md:boardOpen. The fallback works, but a hold that depends on which
           desk happens to be open is a hold that is right by coincidence. */
        const put = await window.mdBoardFilePut(
          { id, dataURL: file.dataURL, mimeType: file.mimeType }, openId);
        if (!put.ok) rejected.push(id);
        else if (put.oversize) stranded = true;
      }
      if (rejected.length && apiRef.current) {
        const api = apiRef.current;
        const all = api.getSceneElementsIncludingDeleted
          ? api.getSceneElementsIncludingDeleted() : api.getSceneElements();
        api.updateScene({ elements: all.map((el) => !el.isDeleted && rejected.includes(el.fileId)
          ? engine.newElementWith(el, { isDeleted: true }) : el) });
        raise({ kind: 'image', text: `That image is too large to store (the limit is about ${mdSizeWords(window.MD_BOARD_MAX_FILE_BYTES)}). Try a smaller one.` });
      }
      else if (stranded) raise({ kind: 'warn', text: 'That image is saved here in full, but it is too big to sync — it will not reach your other devices. A smaller copy of it would.' });
    }, 600);
  }, [openId, engine]);

  /* ── where the board's own controls stand ─────────────────────────────────
     Style is a genuine member of Excalidraw's toolbar row; Reset is an item in
     that row's ⋯ menu now (see syncMore), so what still needs a seat of its
     own is the zoom row where the engine's compact layout draws none. The
     toolbar is engine-owned and can replace its children while the board is
     open, so `syncStyle` below keeps a React portal host in the physical
     leftmost seat of whichever row is live. This is especially important on
     iPad: the Pencil control is mounted only after a stylus is detected, but
     it can never arrive to the left of Style because the host is re-seated
     after every resulting measurement.

     The remaining measurements go onto the wrapper as custom properties.
     `--md-tb-gap` is the symmetric space between the toolbar island and the UI
     layer; the style panel uses it to open below the same island. The footer
     is measured for its position and height only — where a menu item lives,
     there is nothing to measure.

     A ResizeObserver asks the question, and it watches TWO things: the wrapper,
     because the window resizing or the engine crossing into its compact layout
     moves the toolbar; and the toolbar itself, because the toolbar can change
     width while the wrapper does not move a pixel.

     The toolbar is re-observed rather than held, in the same breath as being
     measured, because the engine replaces the element across the compact switch
     — so whatever is answering to that selector now is what is being watched.
     The observer also fires once on attach, which is the first measurement.

     The retry is not belt and braces. The engine renders its UI a few frames
     after the element it lives in exists — it measures the canvas first, and
     keeps the whole layer display:none until it has — so the answer to "where is
     the toolbar" at the moment of any of those events is usually "not yet". So a
     failed measurement asks again next frame, up to a second, and any new resize
     restarts that budget. Until it answers, the stylesheet's fallbacks stand and
     nothing is painted from them: the button is display:none until the engine
     has a style panel to toggle.

     A value is written only when it has changed. Two of these three numbers are
     read back into the layout — the panel hangs off `--md-tb-gap` — so writing
     the same answer on every frame is how a measure/paint pair turns into a
     loop that never settles. Unchanged means nothing to say.

     THE BOTTOM ROW NEEDED A SIGNAL THE OBSERVER COULD NOT GIVE — and the right
     answer in the end was to stop needing the signal. The footer grows and
     shrinks without ever resizing anything being watched: the wrapper is the
     whole canvas and never changes, and the footer element itself is replaced by
     the engine when Undo appears — a ResizeObserver on the node that was there
     before goes on watching something detached from the document, silently, for
     as long as the board is open. So the first drawing on an empty board left
     the measured end of the row at the width it had with no history in it, and
     Reset was painted on top of the Undo button that had just appeared beside
     it. `remeasure` from onChange was the first repair, and it was not enough:
     the SAME overlap came back on an upright iPad, because a measurement can
     also simply never have run. Twice is a pattern, and the pattern is that
     Reset's position was derived from a number instead of from the row. The row
     seat fixed it; the fix's decision has since moved the whole control into
     the ⋯ menu, where no row rule applies at all. What remains measured here
     is the row's height and how far it sits from the bottom, which used to be
     "the parts that can overlap". Nothing here now can.

     AND A DEADLINE IS THE WRONG INSTRUMENT FOR "HAS THE ENGINE DRAWN ITSELF".
     Sixty frames is a second, which is generous against the frame or two the
     engine takes to lay itself out once it is RUNNING — and it is also the whole
     budget for fetching three megabytes of it first. On a slow connection that
     expires before the toolbar exists, and there is nothing left to ask again:
     the ResizeObserver watches the wrapper, which is the whole canvas and does
     not resize. Every property here would stay unset for the life of the board
     and the controls would stand on the stylesheet's fallbacks — right by luck
     at one window size, wrong at every other.

     So the wait is not timed. The engine's UI appearing is a DOM mutation, so
     that is what is watched, and the observer is disconnected the moment there
     is an answer — nothing to pay during an ordinary drawing session, and it
     waits exactly as long as the connection takes. The frame budget stays for
     what it was actually for. */
  const remeasure = useRefB(null);
  useEffectB(() => {
    const wrap = wrapEl || wrapRef.current;
    if (!wrap || !engine) return;
    let raf = 0;
    let tries = 0;
    let watched = null;
    let watchedFooter = null;
    let watchedLayer = null;
    let mo = null;
    const set = (name, px) => {
      const v = `${Math.round(px)}px`;
      if (wrap.style.getPropertyValue(name) !== v) wrap.style.setProperty(name, v);
    };
    /* A physical seat, deliberately. Drawing tools keep the same visual order
       when the surrounding app changes writing direction, and “most left” is
       therefore append in RTL and prepend in LTR. The host itself is stable:
       moving it preserves the portal and Style's focus/open state. */
    const syncStyle = () => {
      const bar = wrap.querySelector('.Island.App-toolbar');
      if (!bar) return null;
      const anchor = bar.querySelector('label:has(>input[data-testid="toolbar-selection"])')
        || bar.querySelector('label.ToolIcon');
      const row = (anchor && anchor.parentElement) || bar;
      let host = styleHostRef.current;
      if (!host) {
        host = document.createElement('div');
        host.className = 'md-style-toolbar-host';
        host.setAttribute('data-md-style-toolbar-host', '');
        styleHostRef.current = host;
        setStyleHost(host);
      }
      const rtl = getComputedStyle(row).direction === 'rtl';
      const seated = host.parentElement === row
        && (rtl ? row.lastElementChild === host : row.firstElementChild === host);
      if (!seated) {
        if (rtl) row.appendChild(host);
        else row.insertBefore(host, row.firstChild);
      }
      return bar;
    };
    /* Reset's old seat was retired the way a move should retire it: not by
       removing the host from this list, but by the ⋯ menu becoming the place a
       wipe can be asked for (see the menu builder below). The zoom row keeps
       the machinery the footer once used for three members. */
    /* ZOOM BUTTONS, BUT ONLY WHERE THE ENGINE DRAWS NONE. `.zoom-actions` is
       rendered inside `.layer-ui__wrapper`, and that wrapper does not exist in
       the engine's compact layout — a phone's answer to "how far out am I" was
       meant to be pinch, and pinch alone can neither say 140% nor come back to
       100%. So the row gets ours instead.

       THE QUESTION IS ASKED OF THE DOM, not of md-layout. "Which layout is
       this" is the wrong question and would answer it wrongly: the gap is the
       engine's, so what settles it is whether the engine has already drawn the
       controls. Where it has, this stands down and the host is taken away
       again — two zoom rows in one board is the failure that prevents.

       At the HEAD of the row, because the engine's own desktop footer reads
       zoom and then history, and Reset appends itself after both. One order in
       both layouts. */
    const syncZoom = () => {
      const own = wrap.querySelector('.zoom-actions');
      const host = zoomHostRef.current;
      /* Desktop Excalidraw puts zoom and history in one otherwise anonymous
         flex row. Name that row so the app can give the WHOLE group the same
         material as the toolbar instead of painting three unrelated pills.
         The engine may rebuild this row, so the repair loop repeats the mark. */
      if (own && own.parentElement) own.parentElement.classList.add('md-board-footer-island');
      const row = own ? null : wrap.querySelector('.App-toolbar-content > div:last-of-type');
      if (!row) {
        if (host && host.parentElement) host.parentElement.removeChild(host);
        return null;
      }
      let seat = host;
      if (!seat) {
        seat = document.createElement('div');
        seat.className = 'md-zoom-row-host';
        seat.setAttribute('data-md-zoom-host', '');
        zoomHostRef.current = seat;
        setZoomHost(seat);
      }
      if (row.firstElementChild !== seat) row.insertBefore(seat, row.firstElementChild);
      return row;
    };
    /* EVERY PIECE OF OUR OWN CHROME IS SEATED HERE, and nowhere else. Style,
       the ⋯ and the zoom row all live in rows the engine owns and rebuilds, so
       they have the same failure mode and deserve one answer to it. Calling
       this is idempotent: each sync re-seats only what has come loose.
       `syncMore` is declared below and that is safe for the same reason `ro`
       is — nothing calls this until a frame has passed. */
    const ensureChrome = () => {
      const bar = syncStyle();
      syncMore();
      syncZoom();
      return bar;
    };
    const measure = () => {
      raf = 0;
      const bar = ensureChrome() || wrap.querySelector('.Island.App-toolbar');
      const footer = wrap.querySelector('.layer-ui__wrapper__footer-left');
      const b = bar && bar.getBoundingClientRect();
      const f = footer && footer.getBoundingClientRect();
      if (!b || !b.width || !f || !f.height) {
        if (tries++ < 60) raf = requestAnimationFrame(measure);
        return;
      }
      /* The observer STAYS. See the note where it is created: this is where it
         used to be disconnected, and that single line is what turned "the
         engine rebuilt its toolbar" into "the board has lost two controls
         until you reload". Its handler is cheap and silent while our chrome is
         standing, which is every stroke of an ordinary drawing session. */
      if (bar !== watched) { if (watched) ro.unobserve(watched); ro.observe(bar); watched = bar; }
      if (footer !== watchedFooter) {
        if (watchedFooter) ro.unobserve(watchedFooter);
        ro.observe(footer);
        watchedFooter = footer;
      }
      const w = wrap.getBoundingClientRect();
      /* ── AND THE LAYER IS WATCHED, BECAUSE IT IS NOW THE ONLY THING THAT
         CHANGES SIZE WHEN THE DAY FOLDS. The wrapper used to be the signal: it
         WAS the strip beside the day, so folding the day resized it and every
         measurement here re-ran. That resize is precisely the defect that was
         removed — the wrapper is the whole window now and never changes — and
         removing it would have taken the wake-up call with it. The toolbar and
         the footer are no help: the inset MOVES them without resizing them by a
         pixel, and a ResizeObserver says nothing about movement.
         Counted in the browser across one fold, with observers on all four:
         layer 1, wrapper 0, toolbar 0, footer 0. The layer's width IS the
         inset, so it is the one box whose size still answers the question. */
      const layer = wrap.querySelector('.layer-ui__wrapper');
      if (layer && layer !== watchedLayer) {
        if (watchedLayer) ro.unobserve(watchedLayer);
        ro.observe(layer);
        watchedLayer = layer;
      }
      /* TWO BOXES, AND THE DIFFERENCE BETWEEN THEM IS THE DAY. The wrapper is
         the whole window — the canvas runs under the day panel so that folding
         the day cannot resize it. The engine's UI layer does NOT run under the
         day: the stylesheet insets it by --md-board-chrome-start so the toolbar
         and the zoom row stay in the clear. So "how much room is there beside
         the toolbar" has to be asked of the layer, which is the box the engine
         actually centres the toolbar in.
         It matters because `--md-tb-gap` is read by `.App-menu__left`, which
         lives INSIDE that layer. Measured against the wrapper the gap would
         carry the inset the layer has already been given, and the style panel
         would be pushed a second panel-width across the board. Falls back to
         the wrapper for the frames before the engine's layer exists, which is
         also every layout where the two boxes are the same box. */
      const lr = (layer || wrap).getBoundingClientRect();
      set('--md-tb-gap', Math.min(b.left - lr.left, lr.right - b.right));
      set('--md-tb-top', b.top - w.top);
      set('--md-tb-h', b.height);
      /* `--md-footer-end` is gone. It existed for exactly one reader — Reset,
         which stood beside the engine's zoom/history row by being told, in
         pixels, where that row ended. Two things were wrong with that. The row's
         width CHANGES (Redo is display:none while there is nothing to redo), so
         the number had to be re-measured to stay true; and the CSS fallback for
         the frames before it was measured was `162px`, which on an upright iPad
         is the exact x of the Undo button. A stale or unmeasured variable
         therefore did not put Reset roughly in the right place, it put Reset ON
         TOP OF Undo. Reset is a member of the ⋯ menu now, where that
         arrangement cannot be wrongly painted at all; what still measures here
         is where the row sits, never its members. */
      set('--md-footer-bottom', w.bottom - f.bottom);
      set('--md-footer-h', f.height);
    };
    /* ── the ⋯ menu, which is the board's ONLY overflow menu ─────────────
       IT IS CONSTANT (Marwan, 2026-08-23). It used to appear only below
       920px portrait, and its contents were computed: a tool earned a seat
       "if and only if its own toolbar node is hidden at the current size".
       Both halves of that were wrong in the same way. A control that is
       present at one width and gone at another cannot be learned — you
       reach for where it was — and a menu whose items depend on the
       viewport cannot be described to anyone, including in a bug report.
       Worse, the rule made the menu's existence depend on a measurement,
       and when the measuring loop went quiet the ⋯ did not fall back to
       something reduced, it fell back to nothing at all.

       So the seat is permanent and the list is a table. The list also
       ABSORBS the engine's own extra-tools dropdown — frame, web embed,
       laser pointer — because two overflow menus sitting one seat apart on
       the same row is the duplication this app keeps removing. The engine's
       trigger is hidden in the stylesheet at every size.

       Mermaid to Excalidraw is dropped. It is an import format rather than
       a drawing tool, it is the one item here that opens a dialog instead
       of picking up a tool, and it shares `data-testid` with Web Embed —
       so it cannot even be addressed by the id the others use.

       Items act through the engine's API where there is one and proxy a
       click to its own node where there is not, so there is still exactly
       one piece of state per tool. Glyphs are CLONED from the engine's
       nodes at open time, so the menu cannot drift from the toolbar's
       icons, and button size, icon size and corner radii are MEASURED from
       the live toolbar rather than re-declared. */
    let manualSyncing = false;
    let deskKeys = [];
    let deskSyncPending = false;
    let deskSyncChecking = false;
    let deskUnreadable = false;
    let syncCheckSeq = 0;
    const durableSyncAvailable = () => typeof window.mdSync === 'function'
      && typeof window.mdHasSessionMark === 'function'
      && window.mdHasSessionMark()
      && navigator.onLine !== false;
    const durableSyncBusy = () => typeof window.mdSyncBusy === 'function' && window.mdSyncBusy();
    const paintSyncItem = () => {
      const syncButton = wrap.querySelector('[data-md-sync-now]');
      if (!syncButton) return;
      /* BEFORE the emptiness is read as agreement. A desk this device cannot
         read offers no documents, so it trivially owes the server none — and
         the control said "up to date" over a blank canvas and a CAN'T BE READ
         tile. Nothing is known about this desk's standing; saying so is the
         only true answer available. */
      const state = !durableSyncAvailable() ? 'unavailable'
        : (deskUnreadable ? 'unreadable'
          : (manualSyncing || (deskSyncPending && durableSyncBusy()) ? 'syncing'
            : (deskSyncChecking ? 'checking' : (deskSyncPending ? 'available' : 'idle'))));
      syncButton.disabled = state !== 'available';
      syncButton.setAttribute('aria-disabled', state === 'available' ? 'false' : 'true');
      syncButton.setAttribute('aria-busy', state === 'syncing' ? 'true' : 'false');
      syncButton.setAttribute('data-md-sync-state', state);
      const label = state === 'syncing' ? 'Syncing…'
        : (state === 'unavailable' ? 'Desk sync unavailable'
          : (state === 'unreadable' ? 'This desk can\u2019t be read'
            : (state === 'checking' ? 'Checking desk sync…'
              : (state === 'idle' ? 'This desk is up to date' : 'Sync now'))));
      syncButton.setAttribute('aria-label', label);
      syncButton.title = label;
      /* Another automatic sync may already own the transport, and this glyph
         has to settle at the instant that one finishes. It used to find that
         instant by asking again in 120 ms, from inside itself, for as long as
         the answer was "syncing" — and the asking is not cheap: it walked every
         desk and every image on the account out of IndexedDB and hashed them,
         eight times a second, on the thread carrying the pencil. mdSync says
         when it is over now (`mdsyncdone`, on every ending it has), and the
         listener below is what settles this. */
    };
    const refreshDeskSyncItem = async () => {
      const seq = ++syncCheckSeq;
      if (!durableSyncAvailable()) {
        deskSyncChecking = false;
        deskSyncPending = false;
        paintSyncItem();
        return;
      }
      deskSyncChecking = true;
      paintSyncItem();
      let pending = false;
      try {
        deskUnreadable = typeof window.mdBoardUnreadable === 'function'
          && window.mdBoardUnreadable(openId) != null;
        if (typeof window.mdBoardSyncKeys === 'function' && typeof window.mdSyncPending === 'function') {
          const keys = await window.mdBoardSyncKeys(openId);
          /* KEPT, because the sync that follows has to be read against them.
             These are exactly the documents this desk is made of, already
             computed; a refusal naming one of them is this desk failing to
             reach the server, and anything else in the batch is not ours. */
          deskKeys = keys;
          pending = await window.mdSyncPending(keys);
        }
      } catch (e) { pending = false; }
      if (seq !== syncCheckSeq) return;
      deskSyncPending = pending;
      deskSyncChecking = false;
      paintSyncItem();
    };
    const closeMore = () => {
      const m = wrap.querySelector('.md-tb-more-menu');
      if (m) m.remove();
    };
    const LOCK_SEL = '.ToolIcon__lock';
    const HAND_SEL = 'label:has(>input[data-testid="toolbar-hand"])';
    const IMG_SEL = 'label:has(>input[data-testid="toolbar-image"])';
    /* The fixed list. `tool` is what the engine's API is asked for; `sel` is
       the node a glyph is cloned from and the fallback click target. The
       engine's dropdown has to be OPEN for its three items to exist in the
       DOM, so they carry their own glyphs and are never cloned. */
    const MD_BOARD_MORE_ITEMS = [
      { label: 'Sync this desk live', live: true },
      { label: 'Sync now', sync: true },
      { label: 'Frame tool', tool: 'frame', sel: '[data-testid="toolbar-frame"]' },
      { label: 'Web Embed', tool: 'embeddable', sel: '[data-testid="toolbar-embeddable"]' },
      { label: 'Laser pointer', tool: 'laser', sel: '[data-testid="toolbar-laser"]' },
      { label: 'Hand tool', tool: 'hand', sel: HAND_SEL },
      { label: 'Insert image', tool: 'image', sel: IMG_SEL },
      { label: 'Lock canvas', tool: null, sel: LOCK_SEL },
    ];
    const syncMore = () => {
      const bar = wrap.querySelector('.Island.App-toolbar');
      const trig = wrap.querySelector('.md-tb-more');
      if (!bar) return;
      const anchor = bar.querySelector('label:has(>input[data-testid="toolbar-selection"])')
        || bar.querySelector('label.ToolIcon');
      const row = (anchor && anchor.parentElement) || bar;
      /* First seat FROM THE RIGHT — physical right in every direction:
         the row's far end in LTR (where the engine never appends) and the
         row's start in RTL. If the row re-renders around the ⋯ — a pencil
         is detected, the extra tools collapse — it is simply re-seated. */
      const seat = (el) => {
        if (getComputedStyle(row).direction === 'rtl') row.insertBefore(el, row.firstChild);
        else row.appendChild(el);
      };
      const seated = (el) => (getComputedStyle(row).direction === 'rtl'
        ? row.firstElementChild === el : row.lastElementChild === el);
      if (trig) {
        if (trig.parentElement !== row || !seated(trig)) seat(trig);
        return;
      }
      /* The toolbar's own metrics, read live: every tool's icon square is
         the same size, and the island's radius is the menu's radius. */
      const metrics = () => {
        const sq = anchor && (anchor.querySelector('.ToolIcon__icon') || anchor);
        const m = { w: 0, h: 0, r: '', svg: 0, menuR: '' };
        if (sq) {
          const cs = getComputedStyle(sq);
          m.w = parseFloat(cs.width) || 0;
          m.h = parseFloat(cs.height) || 0;
          m.r = cs.borderRadius;
          const sv = sq.querySelector('svg');
          if (sv) m.svg = parseFloat(getComputedStyle(sv).width) || 0;
        }
        m.menuR = getComputedStyle(bar).borderRadius;
        return m;
      };
      const dress = (el, m, isMenu) => {
        if (!isMenu && m.w) { el.style.width = m.w + 'px'; el.style.height = m.h + 'px'; }
        el.style.borderRadius = isMenu ? m.menuR : m.r;
        const sv = el.querySelector('svg');
        if (sv && m.svg) { sv.style.width = m.svg + 'px'; sv.style.height = m.svg + 'px'; }
      };
      const t = document.createElement('button');
      t.type = 'button';
      t.className = 'md-tb-more';
      t.setAttribute('aria-label', 'More tools');
      t.setAttribute('aria-haspopup', 'menu');
      t.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.9"/><circle cx="12" cy="12" r="1.9"/><circle cx="12" cy="19" r="1.9"/></svg>';
      dress(t, metrics(), false);
      const item = (label, svg, fn, checked = null) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'md-tb-more-item';
        b.setAttribute('role', checked === null ? 'menuitem' : 'menuitemcheckbox');
        if (checked !== null) {
          b.setAttribute('aria-checked', checked ? 'true' : 'false');
          b.setAttribute('data-md-live-sync', checked ? 'on' : 'off');
        }
        b.setAttribute('aria-label', label); b.title = label;
        b.innerHTML = svg; b.addEventListener('click', fn); return b;
      };
      const glyph = (sel, fallback) => {
        const n = wrap.querySelector(sel);
        const svg = n && n.querySelector('svg');
        return svg ? svg.outerHTML : fallback;
      };
      const IMG_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2.5"/><circle cx="9" cy="10" r="1.6"/><path d="M4.5 18.5 10 13l3.5 3.5L17 13l2.5 2.5"/></svg>';
      /* The wipe glyph, WITHOUT a footer to stand in. It keeps ManDana's flame
         metaphor but fills the same optical box and uses the same 2px stroke
         as the native toolbar glyphs around it. */
      const WIPE_SVG = '<svg class="md-tb-glyph-wipe" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.07-2.14-.22-4.05 2-6 .5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.15.43-2.29 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>';
      /* The three the engine keeps in its own dropdown are drawn here, because
         cloning them would mean opening that dropdown to read it. */
      const GLYPHS = {
        live: '<svg class="md-tb-glyph-live" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="4" width="13" height="10" rx="2"/><path d="M6 19h5M8.5 14v5"/><rect x="17" y="7" width="5" height="12" rx="1.5"/></svg>',
        sync: '<svg class="md-tb-glyph-sync" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 0 0-15-6.7L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 15 6.7l3-2.7"/><path d="M16 16h5v5"/></svg>',
        frame: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M4 8h16M4 16h16M8 4v16M16 4v16"/></svg>',
        embeddable: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="M3 9.5h18"/></svg>',
        laser: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 19 15 9"/><path d="m13 4 7 7-3 1-5-5z"/></svg>',
      };
      /* One act per item: ask the engine's API, and fall back to clicking its
         own node. `setActiveTool` is the same call the toolbar buttons make, so
         the tool lights up in the row exactly as if it had been pressed there. */
      const act = (it) => () => {
        if (it.sync) {
          /* The menu stays open. Closing it here used to remove the only glyph
             that could say the requested sync was still moving. A disabled
             native button cannot enter this path, while these guards cover the
             narrow instant between availability changing and its repaint. */
          if (!durableSyncAvailable() || !deskSyncPending || durableSyncBusy()) return;
          manualSyncing = true;
          paintSyncItem();
          setNotice({ kind: 'ok', text: 'Syncing…' });
          void Promise.resolve(window.mdSync()).then((res) => {
            /* A SYNC IS ok WHEN THE REST OF IT WORKED. The server refuses one
               document at a time — a desk past the size cap, a key it does not
               know — and answers ok for the batch, correctly, because
               everything beside it landed. Reading that as "this desk is up to
               date" told somebody their drawing was safely on the account at
               the exact moment it was the one thing that had not gone. */
            const refused = (res && Array.isArray(res.refused) ? res.refused : [])
              .filter((r) => r && deskKeys.includes(r.key));
            if (res && res.ok && refused.length) {
              setNotice({ kind: 'warn', text: refused.some((r) => r.reason === 'value_too_large')
                ? 'This desk is too big to sync. It is saved here in full \u2014 removing some strokes, or splitting it across two desks, brings it back.'
                : 'The server would not take this desk yet. It is saved here in full, and the next sync offers it again.' });
            }
            else if (res && res.ok) setNotice({ kind: 'ok', text: 'This desk is up to date.' });
            else if (res && res.error === 'busy') {
              if (typeof window.mdSyncSoon === 'function') window.mdSyncSoon(0);
              setNotice({ kind: 'ok', text: 'Sync is already finishing.' });
            } else setNotice({ kind: 'error', text: 'This desk could not sync. It is still saved on this device.' });
          }).catch(() => setNotice({ kind: 'error', text: 'This desk could not sync. It is still saved on this device.' }))
            .finally(() => { manualSyncing = false; void refreshDeskSyncItem(); });
          return;
        }
        closeMore();
        if (it.live) {
          const on = typeof window.mdCollabEnabled === 'function' && window.mdCollabEnabled(openId);
          if (typeof window.mdCollabSetEnabled === 'function') window.mdCollabSetEnabled(openId, !on);
          return;
        }
        if (it.tool) {
          try { if (apiRef.current) { apiRef.current.setActiveTool({ type: it.tool }); return; } }
          catch (e) { /* fall through to the node */ }
        }
        const n = wrap.querySelector(it.sel);
        if (n) n.click();
      };
      t.addEventListener('click', () => {
        if (wrap.querySelector('.md-tb-more-menu')) { closeMore(); return; }
        const menu = document.createElement('div');
        menu.className = 'md-tb-more-menu';
        menu.setAttribute('role', 'menu');
        /* Seat it before asking its direction. A detached menu has not inherited
           the app's RTL/LTR direction yet, so a physical-left rule measured
           before this line would be right only by accident. */
        bar.appendChild(menu);
        /* Every item, every time. No test against the current size — that IS
           the fix, and a menu that is sometimes empty was the old bug's last
           step: the trigger stayed and opened nothing. */
        MD_BOARD_MORE_ITEMS.forEach((it) => {
          const live = !!it.live;
          const checked = live && typeof window.mdCollabEnabled === 'function'
            ? window.mdCollabEnabled(openId) : false;
          const label = live && checked ? 'Stop live desk sync' : it.label;
          const button = item(label,
            it.sync ? GLYPHS.sync : (live ? GLYPHS.live : (GLYPHS[it.tool] || glyph(it.sel, IMG_SVG))),
            act(it), live ? checked : null);
          if (it.sync) button.setAttribute('data-md-sync-now', '');
          menu.appendChild(button);
        });
        /* SYNC DESK IS THE FIRST THING AT THE PHYSICAL RIGHT. This is the
           deliberate durable-sync action, distinct from the Live desk toggle.
           DOM end is right in LTR; DOM start is right in RTL. Moving Sync desk
           here leaves Live desk in Sync desk's former seat. Wipe is seated
           independently at the opposite physical end below. */
        {
          const syncButton = menu.querySelector('[data-md-sync-now]');
          if (syncButton) {
            if (getComputedStyle(menu).direction === 'rtl') {
              menu.insertBefore(syncButton, menu.firstChild);
            } else menu.appendChild(syncButton);
          }
        }
        /* ONE DESTRUCTIVE ACTION, at the PHYSICAL LEFT end of the island.
            The menu deserves an action because a menu of only other
            seats-in-the-toolbar is a list of aliases, and this is what `⋯`
            in a desk should offer: the one thing so consequential it belongs
            hidden on purpose. The fixed tool list is still exactly what
            MD_BOARD_MORE_ITEMS names, and Wipe is read disabled, not absent,
            on an empty scene — a menu whose items
            change with the scene is exactly what the constant comment below
            retired for the tools, and one assessment's worth holds here too.
            It asks the scene at open time, not state at effect time: every
            reference in the effect is a frame's old picture. */
        {
          const wl = item('Wipe desk', WIPE_SVG, () => { closeMore(); wipeBoard(); });
          const api = apiRef.current;
          wl.disabled = !api || window.mdBoardSceneEmpty(api.getSceneElements());
          if (getComputedStyle(menu).direction === 'rtl') menu.appendChild(wl);
          else menu.insertBefore(wl, menu.firstChild);
        }
        const m = metrics();
        menu.style.borderRadius = m.menuR;
        menu.querySelectorAll('.md-tb-more-item').forEach((b) => dress(b, m, false));
        void refreshDeskSyncItem();
        const away = (ev) => {
          if (!menu.contains(ev.target) && ev.target !== t) {
            closeMore(); document.removeEventListener('pointerdown', away, true);
          }
        };
        document.addEventListener('pointerdown', away, true);
      });
      seat(t);
    };
    const schedule = () => { tries = 0; if (!raf) raf = requestAnimationFrame(measure); };
    const ro = new ResizeObserver(schedule);
    ro.observe(wrap);
    /* ── THE REPAIR LOOP IS NEVER THROWN AWAY ──────────────────────────────
       It used to be. `measure()` disconnected this observer the first time it
       got an answer, on the reasoning that "the mutations of an ordinary
       drawing session are none of our business" — true of the canvas, and
       false of the thing that actually broke. Excalidraw OWNS the toolbar
       island and rebuilds it; when it does, our Style host and our ⋯ go with
       it. After that the ResizeObserver was watching a detached node, the
       MutationObserver was gone, and no event left in the application could
       ever notice, let alone repair it. That is why the two controls were
       simply absent on an upright iPad with no way to get them back short of
       a reload.
       So it stays connected for the life of the board, and the cost is paid
       back at the door instead: the handler asks one cheap question — is our
       chrome still standing where we put it — and returns without scheduling
       anything when the answer is yes, which it is for every stroke drawn. */
    const chromeStanding = () => {
      const bar = wrap.querySelector('.Island.App-toolbar');
      if (!bar) return true; // nothing to stand in yet; `tries` still owns this
      const host = styleHostRef.current;
      return !!(host && host.parentElement && bar.contains(host)
        && wrap.querySelector('.md-tb-more'));
    };
    mo = new MutationObserver(() => { if (!chromeStanding()) schedule(); });
    mo.observe(wrap, { childList: true, subtree: true });
    const syncSignal = () => { void refreshDeskSyncItem(); };
    window.addEventListener('online', syncSignal);
    window.addEventListener('offline', syncSignal);
    window.addEventListener('mdsynced', syncSignal);
    window.addEventListener('mdsyncpushed', syncSignal);
    /* THE ENDING, and it is the only one of the three that always arrives. The
       other two are about work done; a sync that pulled nothing and pushed
       nothing raises neither, and that is precisely the ending this glyph is
       waiting for. Without it the item polled itself every 120 ms. */
    window.addEventListener('mdsyncdone', syncSignal);
    remeasure.current = schedule;
    schedule();
    /* AND THE REPAIR LOOP OUTLIVES A SUSPENSION. Everything above is woken by
       an observer, and every one of those wakes goes through `schedule`, which
       does nothing at all while `raf` is set. iOS drops a pending frame when it
       suspends the tab, so a desk put away mid-measurement comes back holding a
       latch nothing can clear: the chrome is never re-seated, the toolbar never
       re-measured, and the observers fire into a function that returns at its
       first line. Which is the desk that came back frozen. See mdOnPageResume. */
    const resumed = window.mdOnPageResume(() => {
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      schedule();
    });
    return () => {
      remeasure.current = null;
      ro.disconnect();
      if (mo) mo.disconnect();
      resumed();
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('online', syncSignal);
      window.removeEventListener('offline', syncSignal);
      window.removeEventListener('mdsynced', syncSignal);
      window.removeEventListener('mdsyncpushed', syncSignal);
      window.removeEventListener('mdsyncdone', syncSignal);
      closeMore();
      const trig = wrap.querySelector('.md-tb-more');
      if (trig) trig.remove();
      const host = styleHostRef.current;
      if (host) host.remove();
      const zh = zoomHostRef.current;
      if (zh) zh.remove();
      /* The hosts are dropped, not merely unseated. They belonged to the
         wrapper that is going away; holding a detached node across a remount is
         how `styleHostRef` came to point at an element in no document, which
         `syncStyle` then declined to replace because it already had one. */
      styleHostRef.current = null; setStyleHost(null);
      zoomHostRef.current = null; setZoomHost(null);
    };
  }, [engine, openId, wrapEl]);

  /* ── and the fold is also asked for directly ──────────────────────────────
     The observer above is the live signal and it does fire. This is the guard
     against the one way it can go quiet, which this file has already been bitten
     by once: the engine REPLACES its own elements, and a ResizeObserver left
     watching the node that used to be there "goes on watching something
     detached from the document, silently" — the sentence the footer row earned.
     If the layer is ever swapped, `watchedLayer` holds the old one, its resizes
     never arrive, and nothing re-observes the new one because re-observing
     happens inside the measurement that is no longer being woken.
     `chromeStart` cannot go quiet that way: it is the app's own state, and it
     changes exactly when the inset does. Same ruling as the footer's — ask for
     the measurement when its answer can have changed, rather than waiting for
     something to notice — and it costs one property compare per fold. */
  useEffectB(() => { if (remeasure.current) remeasure.current(); }, [chromeStart]);

  /* A queued save belongs to the board that was open when it was queued, and
     md-app.jsx has already kicked off that board's save before switching (the
     elements are captured there; the write lands asynchronously) — so on the
     way out of a board, and on the way out of the app, the timer is dropped
     rather than allowed to fire into a stale closure. */
  useEffectB(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, [openId]);

  /* Selection is tracked synchronously and saving stays debounced. They read
     the same callback arguments but want opposite timing: a menu that appeared
     600ms after a click would feel broken, and a save on every pointer move
     would make drawing stutter. Kept outside the JSX tag so the surface remains
     readable to both people and the bounded accessibility scanner. */
  const boardChanged = (elements, appState, files) => {
    const hasContent = !window.mdBoardSceneEmpty(elements);
    setHasBoardContent((before) => before === hasContent ? before : hasContent);
    if (remeasure.current) remeasure.current();
    trackZoom(appState);
    trackCard(elements, appState);
    maskLinks(elements, appState);
    /* Excalidraw currently supplies deleted elements to onChange, and its API
       states that contract explicitly. Read the explicit API anyway: it keeps
       this detector correct if a later engine release narrows the callback to
       visible elements, and it is the only collection a reset may use. */
    const sceneElements = apiRef.current && apiRef.current.getSceneElementsIncludingDeleted
      ? apiRef.current.getSceneElementsIncludingDeleted()
      : elements;
    const localChanges = typeof window.mdCollabSceneChanges === 'function'
      ? window.mdCollabSceneChanges(
        sceneElements, elementVersionsRef.current, remoteVersionsRef.current)
      : [];
    if (localChanges.length && collabRef.current) {
      lastChangedRef.current = localChanges;
      /* Freehand has a lighter lane: one reliable shell, cumulative
         point/pressure tails at an adaptive 60 fps, then the ordinary reliable
         canonical element on pointer-up. Everything heavier stays on the
         coalesced 30 fps scene lane. */
      const strokes = localChanges.filter((element) => drawingFreehandRef.current
        && element && element.type === 'freedraw'
        && !element.isDeleted && Array.isArray(element.points));
      const sceneChanges = localChanges.filter((element) => !strokes.includes(element));
      if (typeof collabRef.current.publishStroke === 'function') {
        strokes.forEach((element) => collabRef.current.publishStroke(element));
      } else {
        sceneChanges.push(...strokes);
      }
      if (sceneChanges.length) collabRef.current.publishScene(sceneChanges);
    }
    publishNewImages(localChanges, files);
    save(sceneElements, appState, files);
  };

  if (failed) {
    return <BoardMessage>
      {failed === 'unbuilt'
        ? 'The drawing engine is only in the built app. Run npm start rather than opening src/index.html.'
        : 'The drawing engine could not be loaded. Check your connection and reload.'}
    </BoardMessage>;
  }
  if (!engine || !openId) return <BoardMessage>Loading the desk…</BoardMessage>;
  /* The scene is still being read back from the store. The engine mounts only
     on what was actually stored — never on an empty placeholder it would then
     save over the real drawing. */
  if (!boot || boot.id !== openId) return <BoardMessage>Loading the desk…</BoardMessage>;

  const { Excalidraw } = engine;
  const scene = boot.scene;
  const files = boot.files;

  return (
    <div ref={setWrap} className={`md-board-canvas${styleOpen ? '' : ' md-style-collapsed'}`}
      /* ⌘Z over the drawing is Excalidraw's, always. It claims the press
         itself on the paths it handles; this says so on the paths where the
         browser answers first, so the app's own undo never reaches in. */
      data-md-own-undo
      aria-hidden={muted ? 'true' : undefined} inert={muted ? '' : undefined}
      style={{ position: 'absolute', inset: 0, background: behind, ...chrome }}>
      {/* `key` remounts the engine when the board changes: initialData is read
          once at mount, so without it switching boards would show the previous
          drawing until something forced a re-render. */}
      <Excalidraw key={openId}
        excalidrawAPI={(api) => { apiRef.current = api; registerApi && registerApi(api); }}
        initialData={{
          elements: scene.elements,
          /* Two defaults, on either side of the stored state, and the side each
             one sits on is the whole of what it means.

             BEFORE: thin. The stroke width IS saved — it is in APP_STATE_KEEP —
             so a board that has one keeps it. Forcing thin on every open would
             make the picker a control that forgets: choose bold, come back,
             thin again. This says what a board starts life with and then gets
             out of the way.

             AFTER: the tool. This used to be the PEN, on the argument that a
             board is opened to put something on, so the first drag should be a
             stroke rather than a marquee. That argument was sound while a board
             held nothing but drawing.

             IT IS NOT SOUND NOW, and the way it failed is worth keeping. An
             Excalidraw embeddable becomes interactive only under the SELECTION
             tool — with a drawing tool in hand, a click on one is a click on the
             canvas, and the engine dutifully draws a dot on top of the video.
             So a board full of playable cards opened in a mode where none of
             them could be played, and the symptom was not "the wrong tool is
             armed", it was "the videos are dead": you click a play button, a
             mark appears on it, and nothing happens.

             A board that holds things you can watch has to open able to touch
             them. Drawing is one keystroke away and the toolbar is right there;
             a video that cannot be played is not one keystroke away from
             anything. Selection is also Excalidraw's own default, so this is a
             return to it rather than an invention.

             The tool goes HERE and not through `setActiveTool` on the API,
             which is the obvious way and does not work: the API handle is
             offered synchronously on mount, and the engine applies initialData
             a microtask later, so anything set from that callback is overwritten
             a tick after it lands. Verified, not assumed. */
          appState: { currentItemStrokeWidth: 1, ...scene.appState,
            viewBackgroundColor: paper, theme: light ? 'light' : 'dark',
            activeTool: { type: 'selection' } },
          files: Object.fromEntries(Object.entries(files).map(([id, f]) => [id, {
            id, dataURL: f.dataURL, mimeType: f.mimeType, created: f.created,
          }])),
          scrollToContent: true,
        }}
        theme={light ? 'light' : 'dark'}
        langCode="en"
        onChange={boardChanged}
        onPointerDown={() => {
          lastChangedRef.current = [];
          const appState = apiRef.current && apiRef.current.getAppState();
          drawingFreehandRef.current = appState?.activeTool?.type === 'freedraw';
        }}
        onPointerUp={() => {
          if (lastChangedRef.current.length && collabRef.current) {
            collabRef.current.publishScene(lastChangedRef.current, { final: true });
          }
          lastChangedRef.current = [];
          drawingFreehandRef.current = false;
        }}
        onPointerUpdate={({ pointer, button }) => {
          const api = apiRef.current;
          const appState = api && api.getAppState();
          if (!collabRef.current || !pointer || !appState) return;
          collabRef.current.publishPointer({
            x: pointer.x, y: pointer.y, button,
            tool: pointer.tool || appState.activeTool?.type || 'pointer',
            selected: Object.keys(appState.selectedElementIds || {}),
          });
        }}
        /* Paste has a prop, so it needs no listener of its own. The contract is
           exact and was read out of the engine's source rather than assumed:
           `if (await this.props.onPaste(…) === false) return` — only a literal
           false stops it, so anything that is not ours must return something
           else and let the engine have its turn. It is AWAITED, which is what
           lets the two asynchronous branches below claim a paste truthfully: a
           promise of false stops the engine just as a false does. A paste has no
           position, so the pin lands where the picker's would.

           A manifestation never reaches here — it is claimed a phase earlier,
           for a reason written where that listener is. This stays what it has
           always been: the link route. */
        onPaste={(data) => {
          const text = (data && typeof data.text === 'string' ? data.text : '').trim();
          return takeUrl(text, null) ? false : true;
        }}
        /* Ours are the only embeddables this board can hold — nothing here ever
           pastes a YouTube link — so the answer is "yes to a ManDana ref and no
           to anything else". Returning a boolean rather than undefined is what
           keeps the engine's own allowlist of third-party embed hosts out of it
           entirely: no remote origin is ever considered, let alone framed. */
        validateEmbeddable={(link) => !!window.mdBoardLinkRef(link) || !!window.mdBoardLinkNavigation?.(link)}
        /* Asked before the iframe, and a non-null answer means the iframe is
           never built (`renderEmbeddable?.(el) ?? <iframe/>` in the engine). So
           a pinned record is a ManDana card and nothing is loaded from the
           network to draw it. */
        renderEmbeddable={(el) => window.mdBoardLinkNavigation?.(el.link)
          ? <PinnedDestination el={el} ctx={ctx} light={light} accent={accent} />
          : <PinnedRecord el={el} ctx={ctx} light={light} onOpen={onOpenRecord}
            onManifestRead={onManifestRead} onAsk={askForManifest}
            scopeRef={scopeRef}
            stagedInstance={stage && stage.instance} stoppedInstances={stoppedInstances} />}
        UIOptions={{
          canvasActions: {
            // Theme lives in Settings now (see app/md-settings.jsx). A second
            // switch for it here would let the canvas and the app disagree.
            toggleTheme: false,
            // And so does the paper it paints — offering a colour picker for
            // something that is not stored would be a control that forgets.
            changeViewBackgroundColor: false,
            // Writing to a file on disk is a desktop-app idea; this board is
            // saved continuously and syncs itself. Export as an image stays,
            // because that is how a drawing leaves the app.
            saveToActiveFile: false,
          },
        }} />

      {/* A portal makes Style an actual toolbar item while its state remains
          owned here. It deliberately uses Excalidraw's ToolIcon structure, so
          its dimensions, hover target and responsive sizing are identical to
          every native tool — including on iPad after Pencil appears. */}
      {styleHost && ReactDOM.createPortal(
        <button className="md-style-toggle ToolIcon ToolIcon_type_button"
          onClick={() => setStyleOpen((v) => !v)}
          aria-label="Style" aria-expanded={styleOpen} title="Style">
          <span className="ToolIcon__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
              <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
              <circle cx="16" cy="7" r="2" /><circle cx="10" cy="17" r="2" />
            </svg>
          </span>
        </button>,
        styleHost,
      )}

      {/* ── zoom, where the engine's compact layout offers none ──────────────
          Three controls in the shape everybody already knows, because it is the
          engine's own on a desktop: out, the readout, in. The readout is a
          button and not a label — pinching is how you get to 143%, and tapping
          the number is how you get back from it. That is the only way back to
          100% on a phone, which is why it is not decoration.

          Disabled at the ends rather than hidden, so the row never changes
          width while somebody is pressing a button in it. */}
      {zoomHost && ReactDOM.createPortal(
        <div className="md-board-zoom">
          <button className="md-board-zoom-step" onClick={() => zoomBy(-1)}
            disabled={zoomPct <= window.MD_BOARD_ZOOM_MIN * 100}
            aria-label="Zoom out" title="Zoom out">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" width="16" height="16" aria-hidden="true">
              <path d="M6 12h12" />
            </svg>
          </button>
          <button className="md-board-zoom-level" onClick={zoomReset}
            aria-label="Reset zoom to 100%" title="Reset zoom to 100%">
            {zoomPct}%
          </button>
          <button className="md-board-zoom-step" onClick={() => zoomBy(1)}
            disabled={zoomPct >= window.MD_BOARD_ZOOM_MAX * 100}
            aria-label="Zoom in" title="Zoom in">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" width="16" height="16" aria-hidden="true">
              <path d="M12 6v12M6 12h12" />
            </svg>
          </button>
        </div>,
        zoomHost,
      )}

      {/* ── the board's chrome gaps ─────────────────────────────────────────
          Wipe lives in the ⋯ menu now (see syncMore); what remains here is
          masking over the engine's link badges, which has never had a seat
          of its own — see maskLinks. Above the canvases and below every
          control, touching nothing. */}

      {(collabPeers.length > 0 || collabState === 'connecting' || collabState === 'retrying') &&
      <div role="status" aria-live="polite"
        aria-label={collabPeers.length
          ? `${collabPeers.length} other ${collabPeers.length === 1 ? 'device' : 'devices'} on this desk: ${collabPeers.map((p) => p.name).join(', ')}`
          : 'Reconnecting this desk'}
        data-md-collaborators="true"
        style={{ position: 'absolute', zIndex: 12, top: 'calc(var(--md-top-inset, 0px) + 12px)',
          insetInlineEnd: 14, minHeight: 36, maxWidth: 'min(48%, 360px)', padding: '5px 11px 5px 6px',
          display: 'flex', alignItems: 'center', gap: 8, borderRadius: 999,
          border: `0.75px solid ${inkB(light ? .1 : .16)}`,
          background: light ? 'rgba(255,255,255,.84)' : 'rgba(20,22,28,.8)',
          backdropFilter: 'blur(20px) saturate(150%)', WebkitBackdropFilter: 'blur(20px) saturate(150%)',
          boxShadow: light
            ? '0 1px 2px rgba(16,18,24,.05), 0 10px 28px rgba(16,18,24,.12)'
            : '0 1px 2px rgba(0,0,0,.28), 0 10px 28px rgba(0,0,0,.34)',
          pointerEvents: 'none' }}>
        {collabState === 'retrying' || collabState === 'connecting'
          /* A single quiet pulse while the desk finds its peers again, rather
             than a stack of avatars for people who may not return. */
          ? <span aria-hidden="true" className="md-collab-pulse"
              style={{ width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center',
                background: rgbaB(accent, light ? .16 : .22) }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: accent,
                animation: 'mdCollabPulse 1.6s ease-in-out infinite' }} />
            </span>
          : collabPeers.slice(0, 3).map((peer, index) =>
            <span key={peer.actor} title={peer.name} aria-hidden="true"
              style={{ width: 26, height: 26, marginInlineStart: index ? -11 : 0, flexShrink: 0,
                borderRadius: '50%', display: 'grid', placeItems: 'center', color: '#fff',
                background: mdCollabColour(peer.actor),
                border: `2px solid ${light ? '#fff' : '#171920'}`,
                boxShadow: '0 1px 3px rgba(0,0,0,.18)',
                fontSize: 10.5, fontWeight: 800, letterSpacing: '.02em' }}>
              {(peer.name || 'D').trim().slice(0, 1).toUpperCase()}
            </span>)}
        {collabState !== 'retrying' && collabState !== 'connecting' && collabPeers.length > 3 &&
          <span aria-hidden="true" style={{ fontSize: 11, fontWeight: 750,
            color: inkB(.62), marginInlineStart: -3 }}>+{collabPeers.length - 3}</span>}
        <span aria-hidden="true" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          fontSize: 11.5, fontWeight: 680, color: inkB(.67), letterSpacing: '-.01em' }}>
          {collabState === 'retrying' || collabState === 'connecting'
            ? 'Reconnecting…'
            : collabPeers.map((p) => p.name).slice(0, 2).join(', ')}
        </span>
      </div>}

      {/* The patches over the engine's link badges — see maskLinks. Above the
          canvases and below every control, touching nothing. */}
      <div ref={maskRef} aria-hidden="true"
        style={{ position: 'absolute', inset: 0, zIndex: 5, pointerEvents: 'none' }} />

      {/* ── the selected card's own control ──────────────────────────────────
          One button, on the card, where the thing it acts on is. It rides the
          element through every drag and zoom because `card` is recomputed from
          the same numbers the engine draws with.

          It sits INSIDE the top-right corner rather than floating above the
          selection, which is the arrangement the engine's own pill got wrong:
          a bar hovering off the edge of a card belongs to nothing, and on a
          card near the top of the canvas it lands off-screen. */}
      {card && <>
        {/* Expand sits BESIDE the ⋯ and outside the engine's overlay for the
            same reason the ⋯ does: an embeddable's own DOM gets no pointer
            events until it is the active one, so a control drawn inside the card
            would be dead exactly when somebody first reaches for it. */}
        {cardManifest && <button onClick={openStage}
          aria-label="Open full screen" title="Open full screen"
          style={{ position: 'absolute', zIndex: 13,
            left: card.x + card.w - 66, top: card.y + 10,
            width: 26, height: 26, display: 'grid', placeItems: 'center',
            borderRadius: 8, cursor: 'pointer',
            border: `0.75px solid ${rgbaB('#ffffff', 0.22)}`,
            background: 'rgba(18,20,28,0.62)',
            backdropFilter: 'blur(18px) saturate(160%)',
            WebkitBackdropFilter: 'blur(18px) saturate(160%)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.34)', color: '#ffffff', padding: 0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 3H3v6M15 3h6v6M9 21H3v-6M15 21h6v-6" />
          </svg>
        </button>}

        <button onClick={() => setCardMenu((v) => !v)}
          aria-label="Card options" aria-haspopup="menu" aria-expanded={cardMenu}
          title="Options"
          style={{ position: 'absolute', zIndex: 13,
            left: card.x + card.w - 34, top: card.y + 10,
            width: 26, height: 26, display: 'grid', placeItems: 'center',
            borderRadius: 8, cursor: 'pointer',
            border: `0.75px solid ${rgbaB('#ffffff', 0.22)}`,
            background: cardMenu ? rgbaB(accent, 0.9) : 'rgba(18,20,28,0.62)',
            backdropFilter: 'blur(18px) saturate(160%)',
            WebkitBackdropFilter: 'blur(18px) saturate(160%)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.34)',
            color: '#ffffff', padding: 0,
            transition: 'background 140ms ease' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="6" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="18" cy="12" r="1.7" />
          </svg>
        </button>

        {cardMenu && <>
          <div aria-hidden="true" onClick={closeMenu}
            style={{ position: 'fixed', inset: 0, zIndex: 13 }} />
          <div role="menu" aria-label="Card options"
            style={{ position: 'absolute', zIndex: 14,
              /* Anchored to the button and clamped to the canvas, so a card at
                 the right-hand edge opens its menu inward instead of into the
                 gutter. */
              left: Math.max(8, Math.min(card.x + card.w - 214, (wrapRef.current ? wrapRef.current.clientWidth : 9999) - 222)),
              top: card.y + 42,
              width: 214, padding: '5px 0', borderRadius: 15,
              border: `0.75px solid ${inkB(light ? 0.1 : 0.16)}`,
              background: light ? 'rgba(255,255,255,0.95)' : 'rgba(26,28,36,0.95)',
              backdropFilter: 'blur(26px)', WebkitBackdropFilter: 'blur(26px)',
              boxShadow: '0 18px 44px rgba(0,0,0,0.34)', fontFamily: 'var(--md-body)' }}>
            {cardManifest && <CardItem label="Open full screen" onClick={openStage} />}
            {cardTarget && <CardItem label="Open destination" onClick={() => {
              setCardMenu(false); window.mdNavigationNavigate?.(cardTarget, { source: 'desk' });
            }} />}
            {cardFound && !cardManifest && <CardItem label="Open record" onClick={() => {
              setCardMenu(false); onOpenRecord && onOpenRecord(cardFound);
            }} />}
            <CardItem label="Reset size" onClick={resetSize} />
            {cardEmbed && cardEmbed.href && <CardItem label="Copy link" onClick={copyMediaLink} />}
            {/* What this manifestation may reach. Written as three states rather
                than a toggle, because "allowed to read" and "allowed to change"
                are different enough that a single switch would be a lie. */}
            {cardManifest && <>
              <div style={{ height: 1, margin: '5px 10px',
                background: inkB(light ? 0.08 : 0.12) }} />
              <div style={{ padding: '4px 14px 2px', fontSize: 10.5, fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase', color: inkB(0.38) }}>
                May reach your records
              </div>
              {/* WHAT, exactly. "Allow reading" without naming the library and
                  the columns is a question nobody can answer, and the answer is
                  already in the file — this only says it out loud. */}
              {manifestReach && (manifestReach.sources.length > 0 || manifestReach.actions.length > 0) &&
                <div style={{ padding: '1px 14px 6px' }}>
                  {manifestReach.sources.map((s) =>
                    <ReachRow key={`s:${s.name}`} verb="Reads" row={s} />)}
                  {manifestReach.actions.map((a) =>
                    <ReachRow key={`a:${a.name}`} verb="Changes" row={a} />)}
                </div>}
              {/* And what is different from the last time this was answered.
                  Additions first and on their own line, because they are the
                  only part that needs a decision. */}
              {reachDiff && reachDiff.changed && <>
                <div style={{ padding: '2px 14px 2px', fontSize: 10.5, fontWeight: 700,
                  letterSpacing: '0.08em', textTransform: 'uppercase', color: inkB(0.38) }}>
                  Changed since you allowed it
                </div>
                {/* Full-strength ink and no new colour: this menu has one
                    emphasis scale already, and a bespoke amber here would be a
                    palette decision made inside a permission prompt. */}
                <div style={{ padding: '1px 14px 6px' }}>
                  {/* Keyed by position, not by the sentence: two handles bound
                      to one library can widen by the same column and produce
                      the same words twice, and identical keys would silently
                      drop one of them — losing a line from the only list whose
                      job is to be complete. */}
                  {[...reachDiff.addedSources.map((n) => `Now also reads ${n}`),
                    ...reachDiff.addedActions.map((n) => `Now also changes ${n}`),
                    ...reachDiff.widened.map((w) => `${w.source} — now also ${w.fields.join(', ')}`)]
                    .map((line, i) => <span key={`+${i}`} style={{ display: 'block', fontSize: 11.5,
                      fontWeight: 620, lineHeight: 1.5, color: inkB(0.88) }}>{line}</span>)}
                  {reachDiff.removedSources.concat(reachDiff.removedActions).map((n, i) =>
                    <span key={`-${i}`} style={{ display: 'block', fontSize: 11.5, fontWeight: 500,
                      lineHeight: 1.5, color: inkB(0.4) }}>{`No longer reaches ${n}`}</span>)}
                </div></>}
              {cardGrant && cardGrant.level !== 'none'
                ? <CardItem label={cardGrant.level === 'write' ? 'Reading and changing' : 'Reading only'}
                    hint="Tap to stop allowing this" onClick={clearGrant} />
                : <>
                    {manifestMayRead && <CardItem label="Allow reading" onClick={() => setGrant('read')} />}
                    {manifestMayWrite && <CardItem label="Allow reading and changing" onClick={() => setGrant('write')}
                      hint="It can edit your writing" />}
                    {!manifestMayRead && !manifestMayWrite &&
                      <CardItem label="No journal access requested" onClick={() => setCardMenu(false)} />}
                  </>}
              <div style={{ height: 1, margin: '5px 10px',
                background: inkB(light ? 0.08 : 0.12) }} />
              {stoppedInstances.has(cardManifest.instance)
                ? <CardItem label="Restart Craft" onClick={() => {
                    setStoppedInstances((prev) => { const next = new Set(prev);
                      next.delete(cardManifest.instance); return next; });
                    setCardMenu(false);
                  }} />
                : <CardItem label="Stop Craft" onClick={() => {
                    setStoppedInstances((prev) => new Set(prev).add(cardManifest.instance));
                    if (stage && stage.instance === cardManifest.instance) setStage(null);
                    setCardMenu(false);
                  }} />}
              <CardItem label="Reset private data" hint="Keeps the Craft itself"
                onClick={() => {
                  if (window.mdMStoreDelete) window.mdMStoreDelete(cardManifest.instance);
                  setCardMenu(false);
                  setNotice({ kind: 'ok', text: `“${cardManifest.name}” data was reset.` });
                }} />
            </>}
            <div style={{ height: 1, margin: '5px 10px',
              background: inkB(light ? 0.08 : 0.12) }} />
            <CardItem label="Remove from desk" onClick={removeFromBoard}
              hint={cardManifest ? 'It stays in Crafts' : 'The record stays in its day'} />
            {cardFound && !cardManifest && onDeleteRecord && <CardItem label="Delete record" danger
              hint="Deletes the writing too"
              /* Undo has to put BOTH halves back. This deletes two things — the
                 card here and the writing in the day — and an undo that restored
                 only the record would leave the person looking at a board that
                 had quietly rearranged itself. So the element is handed to the
                 app as a way back, and the one toast undoes the whole act. */
              onClick={() => {
                setCardMenu(false);
                const api = apiRef.current;
                const el = cardEl();
                if (!api || !el || !engine.newElementWith) return;
                const all = api.getSceneElementsIncludingDeleted
                  ? api.getSceneElementsIncludingDeleted() : api.getSceneElements();
                const removed = engine.newElementWith(el, { isDeleted: true });
                api.updateScene({ elements: all.map((e) => e.id === el.id ? removed : e) });
                setCard(null);
                onDeleteRecord(cardFound, () => {
                  const a = apiRef.current;
                  if (!a) return;
                  const current = a.getSceneElementsIncludingDeleted
                    ? a.getSceneElementsIncludingDeleted() : a.getSceneElements();
                  const restored = engine.newElementWith(removed, { isDeleted: false });
                  a.updateScene({ elements: current.map((e) => e.id === el.id ? restored : e) });
                });
              }} />}
          </div>
        </>}
      </>}

      {/* Floating rather than a row of its own: the board has no chrome left to
          hang a message on, and a permanent strip reserved for something that
          is almost never true is exactly the furniture this change removed. */}
      {/* Below the toolbar, not on top of it. Both are centred at the top of the
          canvas, so at 14px this sat squarely over the tool row — and the
          longer, more useful oversize message made it cover the whole thing.
          The measured toolbar is already published as CSS variables for the
          style toggle; this rides the same measurement. See the effect above. */}
      {/* ── the stage ────────────────────────────────────────────────────────
          An overlay of the app's own, over the whole board surface — the same
          shape and the same slot the compose sheet uses when a record opens on
          the canvas. It carries the ONLY live copy of this manifestation while
          it is open; the card behind it is showing a placeholder. */}
      {stage && window.CraftFrame && (
        <div role="dialog" aria-modal="true" aria-label={stage.name}
          style={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex',
            flexDirection: 'column', padding: 14, gap: 10,
            background: light ? 'rgba(244,246,251,0.82)' : 'rgba(10,11,16,0.82)',
            backdropFilter: 'blur(30px) saturate(150%)',
            WebkitBackdropFilter: 'blur(30px) saturate(150%)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <span style={{ flex: 1, minWidth: 0, color: inkB(0.72), fontSize: 13.5, fontWeight: 620,
              fontFamily: 'var(--md-body)', whiteSpace: 'nowrap', overflow: 'hidden',
              textOverflow: 'ellipsis' }}>{stage.name}</span>
            {/* It already wore a ring — it was the one surface that had worked
                this out on its own — but the ring held an × and the stroke was
                its own 2.2. It draws the app's mark now, and keeps the frosted
                ground it needs to be legible over a board. */}
            <window.MdDismiss ref={stageCloseRef} onClick={closeStage} label="Close (Esc)"
              style={{ color: inkB(0.75),
                background: light ? 'rgba(255,255,255,0.7)' : 'rgba(26,28,36,0.7)' }} />
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            {/* Keyed on the instance so the stage's copy is its OWN mount rather
                than React reconciling it with the card's — which would be a DOM
                move, and a DOM move reloads an iframe anyway. */}
            <window.CraftFrame key={`stage:${stage.instance}`} entry={stage}
              light={light} title={stage.name}
              onRead={onManifestRead} onAsk={askForManifest} surface="stage"
              scopeRef={scopeRef}
              stopped={stoppedInstances.has(stage.instance)} />
          </div>
        </div>)}

      {/* 1.18 · WHERE THE FILE WAS DROPPED. A 300 MB PDF dropped on a desk
          spends minutes going up, and the only place it was ever reported was
          Settings — a screen nobody is on, having just dropped something on a
          desk. Same component as the account-wide list in 5.5, at a second
          mount point, so an upload paused here is found paused there.

          It shows only what THIS desk owns: the refs on a queue row are the
          records that own the bytes, and a picture uploading for somebody
          else's desk is not this desk's business. Chunks never appear —
          mdBlobTransfers answers one row per file. */}
      {transfers.length > 0 &&
      <div style={{ position: 'absolute',
        top: `calc(var(--md-tb-top, 16px) + var(--md-tb-h, 44px) + ${notice ? 66 : 10}px)`,
        left: '50%', transform: 'translateX(-50%)',
        zIndex: 12, width: 'min(420px, calc(100% - 40px))',
        padding: 8, borderRadius: 16,
        background: light ? 'rgba(255,255,255,0.92)' : 'rgba(28,30,38,0.92)',
        border: `0.75px solid ${inkB(light ? 0.1 : 0.16)}`,
        backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)',
        boxShadow: '0 14px 36px rgba(0,0,0,0.24)' }}>
        <window.MdTransfers rows={transfers} onAct={onTransferAct} />
      </div>}

      {notice &&
      <div role="status" style={{ position: 'absolute',
        top: 'calc(var(--md-tb-top, 16px) + var(--md-tb-h, 44px) + 10px)',
        left: '50%', transform: 'translateX(-50%)',
        zIndex: 12, maxWidth: 'min(520px, calc(100% - 40px))',
        display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', borderRadius: 14,
        background: notice.kind === 'error'
          ? (light ? 'rgba(225,29,72,0.14)' : 'rgba(244,63,94,0.2)')
          : (light ? 'rgba(255,255,255,0.92)' : 'rgba(28,30,38,0.92)'),
        border: `0.75px solid ${inkB(light ? 0.1 : 0.16)}`,
        backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)',
        boxShadow: '0 14px 36px rgba(0,0,0,0.24)',
        color: inkB(0.82), fontSize: 12.5, lineHeight: 1.5, fontWeight: 550 }}>
        <span style={{ flex: 1 }}>{notice.text}</span>
        <button onClick={() => { dismissed.current = notice.text; setNotice(null); }} aria-label="Dismiss"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            color: 'inherit', opacity: 0.55, lineHeight: 0 }}>{MDIB.close(inkB(0.72), 15)}</button>
      </div>}
    </div>
  );
}

/* ONE THING A MANIFESTATION ASKED FOR, said out loud before anybody agrees to
   it (Phase 1D-1b). Not a menu item: nothing here is clickable, because none of
   it is a choice — it is the sentence the choice underneath is about.

   The library on the first line and its columns on the second, in the person's
   own names for both, resolved by mdManifestReach from the bytes that will
   actually run. Deliberately quieter than CardItem's label: the decision is
   still "Allow reading", and this must read as its subject rather than compete
   with it. No new colour — the danger ink is the file's existing one. */
function ReachRow({ verb, row }) {
  const light = window.mdIsLight();
  /* An empty field list is not "everything", it is NOTHING: the projection in
     md-manifest.ts copies only declared fields, so such a source sees that rows
     exist and not one value inside them. Saying "no columns" is the honest
     reading and it is the friendlier one; leaving the line blank would let a
     person supply the opposite meaning themselves. */
  const detail = row.fields.length
    ? row.fields.join(' · ')
    : 'no columns — only that rows exist';
  return (
    <div style={{ padding: '2px 0' }}>
      <span style={{ display: 'block', fontSize: 12, fontWeight: 600, lineHeight: 1.4,
        color: inkB(0.78) }}>{`${verb} ${row.database}`}</span>
      <span style={{ display: 'block', fontSize: 11, fontWeight: 500, lineHeight: 1.45,
        color: inkB(0.42) }}>{detail}</span>
      {/* A library this device does not hold. Worth its own line and the
          danger ink: the rest of the row is describing something the person
          cannot check, and a permissions panel that renders a missing library
          in the same voice as a present one is quietly overstating what it
          knows. */}
      {row.missing && <span style={{ display: 'block', fontSize: 11, fontWeight: 560,
        lineHeight: 1.45, color: light ? '#c0263f' : '#ff6b81' }}>
        Not on this device — nothing here can be shown to you
      </span>}
    </div>);
}

/* One row of the card menu. The hint is not decoration: the two destructive
   items differ only in what they destroy, and a line of plain language under
   each is the difference between a considered choice and a coin toss. */
function CardItem({ label, hint, onClick, danger = false }) {
  const light = window.mdIsLight();
  const [hot, setHot] = useStateB(false);
  return (
    <button role="menuitem" onClick={onClick}
      onMouseEnter={() => setHot(true)} onMouseLeave={() => setHot(false)}
      onFocus={() => setHot(true)} onBlur={() => setHot(false)}
      style={{ display: 'block', width: '100%', textAlign: 'start',
        padding: hint ? '7px 14px 8px' : '8px 14px', border: 'none',
        background: hot ? inkB(light ? 0.05 : 0.08) : 'transparent',
        cursor: 'pointer', fontFamily: 'inherit' }}>
      <span style={{ display: 'block', fontSize: 13.5, fontWeight: 560,
        color: danger ? (light ? '#c0263f' : '#ff6b81') : inkB(0.88) }}>{label}</span>
      {hint && <span style={{ display: 'block', fontSize: 11.5, marginTop: 1.5,
        color: inkB(0.42), fontWeight: 500 }}>{hint}</span>}
    </button>);
}

function BoardMessage({ children }) {
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', padding: 40 }}>
      <span style={{ color: window.ink(0.3), fontSize: 13, fontWeight: 550, letterSpacing: 0.3,
        textAlign: 'center', maxWidth: 420, lineHeight: 1.6 }}>
        {children}
      </span>
    </div>
  );
}

/* MediaFrame is shared with md-compose.jsx, which draws the same player under a
   record's writing. One component, so a video cannot come to look like two
   different things depending on which surface you opened it from. */
Object.assign(window, {
  BoardCanvas, BoardList, useBoards, MediaFrame, FileFrame,
});
